import { combineReducers } from "redux";
import { getProvincias } from "../../utils/modalDataUtils";
import apiFetch from "../../utils/api";
import tabsReducer, {
  createTab,
  deleteTab,
  switchTab,
  updateTabOrder,
  updateTabEditableData,
  updateTabValidationErrors,
  updateTabTaricErrors,
  updateTabSelectedAgent,
  updateTabSelectedSender,
  updateTabSelectedReceiver,
  updateTabStatus,
  updateTabIsDirty,
  migrateArrayToTabs,
  resetTabs,
} from "./tabsReducer";
import workflowSessionsReducer, {
  addToProcessingQueue,
  removeFromProcessingQueue,
  setCurrentlyProcessingSession,
  clearProcessingQueue,
  createWorkflowSession,
  deleteWorkflowSession,
  switchWorkflowSession,
  updateWorkflowSessionState,
  updateWorkflowSessionName,
} from "./workflowSessionsReducer";
import uiReducer, {
  setShowBackToDashboardButton,
  setRateLimit,
} from "./uiReducer";

const initialUserState = {
  userData: {
    id: 45,
    companyName: "Atlantis Digital",
    address: "Kinderhuissingel 6a 2013 AS Haarlem",
    taxId: "123-45-6789",
    eori: "EORI123456",
    city: "Haarlem",
    postalCode: "12345",
    countryCode: "NL",
    customsOffice: "Atlantis Digital Office",
  },
  selectedSender: [], // Array of senders, one per export index
  selectedReceiver: [], // Array of receivers, one per export index
  selectedAgent: [], // Array of agents, one per export index
};

const initialCompanyState = {
  nombre: null,
  nif: null,
  eori: null,
  direccion: null,
  localidad: null,
  pais: null,
  codigo_postal: null,
  aduana_habitual: null,
  correo_electronico: null,
  needsCompanyCreation: false,
};

// Company reducer
const companyReducer = (state = initialCompanyState, action) => {
  switch (action.type) {
    case "SET_COMPANY":
      return { ...action.payload, needsCompanyCreation: false };
    case "UPDATE_COMPANY":
      return { ...state, ...action.payload };
    case "SET_NEEDS_COMPANY_CREATION":
      return { ...state, needsCompanyCreation: action.payload };
    default:
      return state;
  }
};

// User reducer
const userReducer = (state = initialUserState, action) => {
  switch (action.type) {
    case "UPDATE_USER":
      return { ...state, userData: action.payload };
    case "SET_SELECTED_SENDER":
      // action.payload should be { index: number, sender: object }
      if (action.payload && typeof action.payload.index === 'number') {
        const newSenders = [...(state.selectedSender || [])];
        newSenders[action.payload.index] = action.payload.sender;
        return { ...state, selectedSender: newSenders };
      }
      // Backward compatibility: if payload is just the sender object, treat as index 0
      const newSendersCompat = [...(state.selectedSender || [])];
      newSendersCompat[0] = action.payload;
      return { ...state, selectedSender: newSendersCompat };
    case "SET_SELECTED_RECEIVER":
      if (action.payload && typeof action.payload.index === 'number') {
        const newReceivers = [...(state.selectedReceiver || [])];
        newReceivers[action.payload.index] = action.payload.receiver;
        return { ...state, selectedReceiver: newReceivers };
      }
      const newReceiversCompat = [...(state.selectedReceiver || [])];
      newReceiversCompat[0] = action.payload;
      return { ...state, selectedReceiver: newReceiversCompat };
    case "SET_SELECTED_AGENT":
      if (action.payload && typeof action.payload.index === 'number') {
        const newAgents = [...(state.selectedAgent || [])];
        newAgents[action.payload.index] = action.payload.agent;
        return { ...state, selectedAgent: newAgents };
      }
      const newAgentsCompat = [...(state.selectedAgent || [])];
      newAgentsCompat[0] = action.payload;
      return { ...state, selectedAgent: newAgentsCompat };
    case "RESET_SELECTED_SENDER":
      return { ...state, selectedSender: [] };
    case "RESET_SELECTED_RECEIVER":
      return { ...state, selectedReceiver: [] };
    case "RESET_SELECTED_AGENT":
      return { ...state, selectedAgent: [] };
    default:
      return state;
  }
};

// Agents reducer
export const agentsReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_AGENTS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_AGENT":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_AGENT":
      if (action.payload) {
        return state.map((agent) =>
          agent.id === action.payload.id ? action.payload : agent
        );
      }
      return state;

    case "DELETE_AGENT":
      return state.filter((agent) => agent.id !== action.payload);

    default:
      return state;
  }
};

// Senders reducer
export const sendersReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_SENDERS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_SENDER":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_SENDER":
      if (action.payload) {
        return state.map((sender) =>
          sender.id === action.payload.id ? action.payload : sender
        );
      }
      return state;

    case "DELETE_SENDER":
      return state.filter((sender) => sender.id !== action.payload);

    default:
      return state;
  }
};

// Receivers reducer
export const receiversReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_RECEIVERS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_RECEIVER":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_RECEIVER":
      if (action.payload) {
        return state.map((receiver) =>
          receiver.id === action.payload.id ? action.payload : receiver
        );
      }
      return state;

    case "DELETE_RECEIVER":
      return state.filter((receiver) => receiver.id !== action.payload);

    default:
      return state;
  }
};

// Grouped Taric JSON Outputs reducer
export const groupedTaricJsonOutputsReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_GROUPED_TARIC_JSON_OUTPUTS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;
    default:
      return state;
  }
};

// Action Types
export const ADD_DOCUMENTS = "ADD_DOCUMENTS";
export const REMOVE_DOCUMENT = "REMOVE_DOCUMENT";
export const CLEAR_DOCUMENTS = "CLEAR_DOCUMENTS";
export const UPLOAD_COMPLETE = "UPLOAD_COMPLETE";
export const RESET_UPLOAD_STATUS = "RESET_UPLOAD_STATUS";
export const SET_UPLOADED_FILES = "SET_UPLOADED_FILES";
export const SET_STEPS_COMPLETED = "SET_STEPS_COMPLETED";
export const RESET_DOCUMENTS_STATE = "RESET_DOCUMENTS_STATE";
export const SET_EXTRACTED_DATA = "SET_EXTRACTED_DATA";
export const SET_EDITABLE_DATA = "SET_EDITABLE_DATA";
export const FILTER_DECLARATIONS_BY_ERRORS = "FILTER_DECLARATIONS_BY_ERRORS";
export const TOGGLE_COLLAPSE_FILES = "TOGGLE_COLLAPSE_FILES";

// New action types for declaration editing
export const UPDATE_DECLARATION_FIELD = "UPDATE_DECLARATION_FIELD";
export const UPDATE_DOCUMENT_FIELD = "UPDATE_DOCUMENT_FIELD";
export const UPDATE_ITEM_FIELD = "UPDATE_ITEM_FIELD";
export const SAVE_DECLARATION_CHANGES = "SAVE_DECLARATION_CHANGES";
export const SET_DECLARATION_DIRTY = "SET_DECLARATION_DIRTY";
export const SET_LAST_EDITED_DOCUMENT_FIELD = "SET_LAST_EDITED_DOCUMENT_FIELD";
export const CLEAR_LAST_EDITED_DOCUMENT_FIELD = "CLEAR_LAST_EDITED_DOCUMENT_FIELD";

// Add new action types for selected declaration
export const SET_SELECTED_DECLARATION = "SET_SELECTED_DECLARATION";
export const CLEAR_SELECTED_DECLARATION = "CLEAR_SELECTED_DECLARATION";

// Initial state
const initialDocumentsState = {
  documents: [],
  hasDocuments: false,
  uploadComplete: false,
  uploadedFiles: [],
  stepsCompleted: false,
  extractedData: null,
  editableData: null, // Store editable data in Redux so it's shared across components
  isDirty: false,
  collapseFiles: false,
  lastEditedDocumentField: null, // Track last edited document field for "Apply to All"
  lastEditedDocumentValue: null, // Track last edited document value for "Apply to All"
  lastEditedDocumentDeclarationIndex: null,
  lastEditedDocumentPartidaIndex: null,
  lastEditedDocumentDocumentIndex: null,
  selectedTemplate: null, // Selected template for styling and data
};

// Helper function to create a deep copy of nested arrays/objects Uses structuredClone for better performance and error handling
const deepCopy = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Use structuredClone if available (modern browsers - Chrome 98+, Firefox 94+, Safari 15.4+)
  if (typeof structuredClone !== 'undefined') {
    try {
      return structuredClone(obj);
    } catch (error) {
      // structuredClone failed (e.g., circular refs, unsupported types) - fallback to JSON method
      console.warn('structuredClone failed, falling back to JSON method:', error);
    }
  }
  
  // Fallback for older browsers or when structuredClone fails
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('deepCopy: Error copying object:', error, 'Object:', obj);
    // Return a shallow copy as fallback
    if (Array.isArray(obj)) {
      return [...obj];
    } else if (typeof obj === 'object') {
      return { ...obj };
    }
    return obj;
  }
};

// Documents reducer with declaration editing functionality
const documentsReducer = (state = initialDocumentsState, action) => {
  switch (action.type) {
    case ADD_DOCUMENTS:
      return {
        ...state,
        documents: [...state.documents, ...action.payload],
        hasDocuments: true,
      };

    case REMOVE_DOCUMENT:
      const updatedDocuments = state.documents.filter(
        (doc) => doc.name !== action.payload
      );
      return {
        ...state,
        documents: updatedDocuments,
        hasDocuments: updatedDocuments.length > 0,
      };

    case CLEAR_DOCUMENTS:
      return {
        ...state,
        documents: [],
        hasDocuments: false,
        uploadComplete: false,
        uploadedFiles: [],
      };

    case UPLOAD_COMPLETE:
      return {
        ...state,
        uploadComplete: true,
      };

    case RESET_UPLOAD_STATUS:
      return {
        ...state,
        uploadComplete: false,
      };

    case SET_UPLOADED_FILES:
      return {
        ...state,
        uploadedFiles: action.payload,
      };

    case SET_STEPS_COMPLETED:
      return {
        ...state,
        stepsCompleted: action.payload,
      };

    case SET_EXTRACTED_DATA:
      // Handle null payload - clear extractedData and editableData
      if (action.payload === null || action.payload === undefined) {
        return {
          ...state,
          extractedData: null,
          editableData: null,
          isDirty: false,
        };
      }

      // Validate payload structure with fallbacks
      let validPayload = null;
      
      if (Array.isArray(action.payload)) {
        // Filter out null/undefined/empty objects
        const filteredPayload = action.payload.filter(d => 
          d !== null && 
          d !== undefined && 
          typeof d === 'object' && 
          Object.keys(d).length > 0
        );
        if (filteredPayload.length > 0) {
          validPayload = filteredPayload;
        }
      } else if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        // Single object, convert to array
        validPayload = [action.payload];
      }
      
      // Fallback: If payload is invalid, try to use existing extractedData or return current state
      if (!validPayload || validPayload.length === 0) {
        console.warn('SET_EXTRACTED_DATA: Invalid payload, keeping existing data or clearing');
        // If we have existing data, keep it; otherwise clear
        if (state.extractedData && Array.isArray(state.extractedData) && state.extractedData.length > 0) {
          return state; // Keep existing data
        }
        return {
          ...state,
          extractedData: null,
          editableData: null,
          isDirty: false,
        };
      }

      // Generate incremented expediente values starting from CF00001
      const generateExpedienteForExtracted = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };

      // Check if this is initial extraction (state.extractedData is null/empty) or an update
      const isInitialExtraction = !state.extractedData || 
        (Array.isArray(state.extractedData) && state.extractedData.length === 0);

      // Helper function to check if expediente is in our generated format (CF00001, CF00002, etc.)
      const isGeneratedExpediente = (expediente) => {
        if (!expediente) return false;
        return /^CF\d{5}$/.test(expediente);
      };

      // Process extracted data to add incremented expediente values For initial extraction: always generate new expediente values (ignore API values) For updates: preserve expediente if it's in our format (CF00001), otherwise generate new Use validPayload instead of action.payload
      const processedExtractedData = validPayload.map((declaration, index) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          console.warn(`SET_EXTRACTED_DATA: Invalid declaration at index ${index}, skipping`);
          return null;
        }
        return {
          ...declaration,
          expediente: isInitialExtraction 
            ? generateExpedienteForExtracted(index)  // Always generate for initial extraction
            : (isGeneratedExpediente(declaration?.expediente) 
                ? declaration.expediente  // Preserve if already in our format
                : generateExpedienteForExtracted(index))  // Generate if not in our format
        };
      }).filter(d => d !== null); // Remove any null entries from invalid declarations

      // Check if expediente values already exist in our format (indicating this is an update, not new data)
      const hasExistingExpediente = processedExtractedData.some(decl => isGeneratedExpediente(decl?.expediente));

      // Final validation: ensure processedExtractedData is valid
      if (!processedExtractedData || processedExtractedData.length === 0) {
        console.warn('SET_EXTRACTED_DATA: processedExtractedData is empty after processing');
        return state; // Keep existing state if processing failed
      }

      return {
        ...state,
        extractedData: validPayload, // Use validated payload
        editableData: processedExtractedData, // Set editableData with incremented expediente values
        isDirty: hasExistingExpediente ? state.isDirty : false, // Only reset dirty flag for new data
      };

    case SET_EDITABLE_DATA:
      // PERFORMANCE OPTIMIZATION: Skip expensive validation for frequent updates Only do basic null check - assume data structure is valid if it's an array/object Full validation only happens on initial load, not on every keystroke
      
      // Quick validation - just check if it's null/undefined or has basic structure
      if (action.payload === null || action.payload === undefined) {
        return {
          ...state,
          editableData: null,
        };
      }
      
      // Fast path: if it's already an array with length > 0, trust it's valid This avoids expensive filtering on every keystroke
      if (Array.isArray(action.payload) && action.payload.length > 0) {
        // Only do minimal validation - check first item exists Skip full filtering to avoid performance hit
        return {
          ...state,
          editableData: action.payload,
        };
      }
      
      // Single object case - convert to array
      if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        return {
          ...state,
          editableData: [action.payload],
        };
      }
      
      // Invalid case - keep existing state (don't log warning on every keystroke)
      return state;

    case FILTER_DECLARATIONS_BY_ERRORS:
      // Filter extractedData and editableData to keep only declarations with errors
      const errorIndices = action.payload; // Array of indices to keep
      let filteredExtractedData = state.extractedData;
      let filteredEditableData = state.editableData;
      
      // Helper function to generate expediente
      const generateExpediente = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };
      
      if (Array.isArray(state.extractedData) && errorIndices.length > 0) {
        filteredExtractedData = state.extractedData
          .filter((_, index) => errorIndices.includes(index))
          .map((declaration, newIndex) => ({
            ...declaration,
            expediente: generateExpediente(newIndex), // Regenerate expediente: CF00001, CF00002, etc.
          }));
      }
      
      if (Array.isArray(state.editableData) && errorIndices.length > 0) {
        filteredEditableData = state.editableData
          .filter((_, index) => errorIndices.includes(index))
          .map((declaration, newIndex) => ({
            ...declaration,
            expediente: generateExpediente(newIndex), // Regenerate expediente: CF00001, CF00002, etc.
          }));
      }
      
      return {
        ...state,
        extractedData: filteredExtractedData,
        editableData: filteredEditableData,
      };

    case RESET_DOCUMENTS_STATE:
      return initialDocumentsState;

    case "SET_SELECTED_TEMPLATE":
      return {
        ...state,
        selectedTemplate: action.payload,
      };

    case "CLEAR_SELECTED_TEMPLATE":
      return {
        ...state,
        selectedTemplate: null,
      };

      case "SET_TEMPLATE_SELECT_CALLBACK":
      return {
        ...state,
        templateSelectCallback: action.payload,
      };

    case "CLEAR_TEMPLATE_SELECT_CALLBACK":
      return {
        ...state,
        templateSelectCallback: null,
      };

    // New cases for declaration editing
    case UPDATE_DECLARATION_FIELD: {
      const { declarationIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || field === undefined) {
        console.warn('UPDATE_DECLARATION_FIELD: Invalid payload, missing declarationIndex or field');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_DECLARATION_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy to avoid state mutation
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_DECLARATION_FIELD: declarationIndex ${declarationIndex} out of bounds (length: ${updatedData.length})`);
            return state;
          }
          
          // Validate declaration exists
          if (!updatedData[declarationIndex] || typeof updatedData[declarationIndex] !== 'object') {
            console.warn(`UPDATE_DECLARATION_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          updatedData[declarationIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle case where extractedData is a single object (not in array)
          updatedData[field] = value;
        } else {
          console.warn('UPDATE_DECLARATION_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_DECLARATION_FIELD: Error updating field:', error);
        return state;
      }
    }

    case UPDATE_DOCUMENT_FIELD: {
      const { declarationIndex, documentIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || documentIndex === undefined || field === undefined) {
        console.warn('UPDATE_DOCUMENT_FIELD: Invalid payload, missing required fields');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_DOCUMENT_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: declarationIndex ${declarationIndex} out of bounds`);
            return state;
          }
          
          const declaration = updatedData[declarationIndex];
          if (!declaration || typeof declaration !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          // Validate documentos array
          if (!declaration.documentos || !Array.isArray(declaration.documentos)) {
            console.warn(`UPDATE_DOCUMENT_FIELD: No documentos array in declaration ${declarationIndex}`);
            return state;
          }
          
          if (documentIndex < 0 || documentIndex >= declaration.documentos.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: documentIndex ${documentIndex} out of bounds`);
            return state;
          }
          
          const documento = declaration.documentos[documentIndex];
          if (!documento || typeof documento !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid documento at index ${documentIndex}`);
            return state;
          }
          
          updatedData[declarationIndex].documentos[documentIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle single object case
          if (!updatedData.documentos || !Array.isArray(updatedData.documentos)) {
            console.warn('UPDATE_DOCUMENT_FIELD: No documentos array in extractedData');
            return state;
          }
          
          if (documentIndex < 0 || documentIndex >= updatedData.documentos.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: documentIndex ${documentIndex} out of bounds`);
            return state;
          }
          
          const documento = updatedData.documentos[documentIndex];
          if (!documento || typeof documento !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid documento at index ${documentIndex}`);
            return state;
          }
          
          updatedData.documentos[documentIndex][field] = value;
        } else {
          console.warn('UPDATE_DOCUMENT_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_DOCUMENT_FIELD: Error updating document field:', error);
        return state;
      }
    }

    case UPDATE_ITEM_FIELD: {
      const { declarationIndex, itemIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || itemIndex === undefined || field === undefined) {
        console.warn('UPDATE_ITEM_FIELD: Invalid payload, missing required fields');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_ITEM_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_ITEM_FIELD: declarationIndex ${declarationIndex} out of bounds`);
            return state;
          }
          
          const declaration = updatedData[declarationIndex];
          if (!declaration || typeof declaration !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          // Validate partidas array
          if (!declaration.partidas || !Array.isArray(declaration.partidas)) {
            console.warn(`UPDATE_ITEM_FIELD: No partidas array in declaration ${declarationIndex}`);
            return state;
          }
          
          if (itemIndex < 0 || itemIndex >= declaration.partidas.length) {
            console.warn(`UPDATE_ITEM_FIELD: itemIndex ${itemIndex} out of bounds`);
            return state;
          }
          
          const item = declaration.partidas[itemIndex];
          if (!item || typeof item !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid item at index ${itemIndex}`);
            return state;
          }
          
          updatedData[declarationIndex].partidas[itemIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle single object case
          if (!updatedData.partidas || !Array.isArray(updatedData.partidas)) {
            console.warn('UPDATE_ITEM_FIELD: No partidas array in extractedData');
            return state;
          }
          
          if (itemIndex < 0 || itemIndex >= updatedData.partidas.length) {
            console.warn(`UPDATE_ITEM_FIELD: itemIndex ${itemIndex} out of bounds`);
            return state;
          }
          
          const item = updatedData.partidas[itemIndex];
          if (!item || typeof item !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid item at index ${itemIndex}`);
            return state;
          }
          
          updatedData.partidas[itemIndex][field] = value;
        } else {
          console.warn('UPDATE_ITEM_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_ITEM_FIELD: Error updating item field:', error);
        return state;
      }
    }

    case SAVE_DECLARATION_CHANGES:
      // Validate payload before saving
      if (action.payload === null || action.payload === undefined) {
        console.warn('SAVE_DECLARATION_CHANGES: Invalid payload (null/undefined)');
        return state;
      }
      
      // Validate payload structure
      const isValidPayload = Array.isArray(action.payload) 
        ? action.payload.length > 0 && action.payload.every(d => d && typeof d === 'object')
        : action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0;
      
      if (!isValidPayload) {
        console.warn('SAVE_DECLARATION_CHANGES: Invalid payload structure');
        return state;
      }
      
      return {
        ...state,
        extractedData: action.payload,
        isDirty: false, // Reset dirty flag after saving
      };

    case SET_DECLARATION_DIRTY:
      return {
        ...state,
        isDirty: action.payload,
      };

    case SET_LAST_EDITED_DOCUMENT_FIELD:
      return {
        ...state,
        lastEditedDocumentField: action.payload.field,
        lastEditedDocumentValue: action.payload.value,
        lastEditedDocumentDeclarationIndex: action.payload.declarationIndex,
        lastEditedDocumentPartidaIndex: action.payload.partidaIndex,
        lastEditedDocumentDocumentIndex: action.payload.documentIndex,
      };

    case CLEAR_LAST_EDITED_DOCUMENT_FIELD:
      return {
        ...state,
        lastEditedDocumentField: null,
        lastEditedDocumentValue: null,
        lastEditedDocumentDeclarationIndex: null,
        lastEditedDocumentPartidaIndex: null,
        lastEditedDocumentDocumentIndex: null,
      };

    case TOGGLE_COLLAPSE_FILES:
      return {
        ...state,
        collapseFiles: !state.collapseFiles,
      };

    default:
      return state;
  }
};

// Action Creators
export const addDocuments = (files) => ({
  type: ADD_DOCUMENTS,
  payload: files,
});

export const removeDocument = (fileName) => ({
  type: REMOVE_DOCUMENT,
  payload: fileName,
});

export const clearDocuments = () => ({
  type: CLEAR_DOCUMENTS,
});

export const uploadComplete = () => ({
  type: UPLOAD_COMPLETE,
});

export const resetUploadStatus = () => ({
  type: RESET_UPLOAD_STATUS,
});

export const setUploadedFiles = (files) => ({
  type: SET_UPLOADED_FILES,
  payload: files,
});

export const setStepsCompleted = (completed) => ({
  type: SET_STEPS_COMPLETED,
  payload: completed,
});

export const resetDocumentsState = () => ({
  type: RESET_DOCUMENTS_STATE,
});

export const setExtractedData = (data) => ({
  type: SET_EXTRACTED_DATA,
  payload: data,
});

export const setEditableData = (data) => ({
  type: SET_EDITABLE_DATA,
  payload: data,
});

export const setSelectedTemplate = (template) => ({
  type: "SET_SELECTED_TEMPLATE",
  payload: template,
});

export const clearSelectedTemplate = () => ({
  type: "CLEAR_SELECTED_TEMPLATE",
});

export const setTemplateSelectCallback = (callback) => ({
  type: "SET_TEMPLATE_SELECT_CALLBACK",
  payload: callback,
});

export const clearTemplateSelectCallback = () => ({
  type: "CLEAR_TEMPLATE_SELECT_CALLBACK",
});

// New action creators for declaration editing
export const updateDeclarationField = (declarationIndex, field, value) => ({
  type: UPDATE_DECLARATION_FIELD,
  payload: {
    declarationIndex,
    field,
    value,
  },
});

export const updateDocumentField = (
  declarationIndex,
  documentIndex,
  field,
  value
) => ({
  type: UPDATE_DOCUMENT_FIELD,
  payload: {
    declarationIndex,
    documentIndex,
    field,
    value,
  },
});

export const updateItemField = (declarationIndex, itemIndex, field, value) => ({
  type: UPDATE_ITEM_FIELD,
  payload: {
    declarationIndex,
    itemIndex,
    field,
    value,
  },
});

export const saveDeclarationChanges = (data) => ({
  type: SAVE_DECLARATION_CHANGES,
  payload: data,
});

export const setDeclarationDirty = (isDirty) => ({
  type: SET_DECLARATION_DIRTY,
  payload: isDirty,
});

export const setLastEditedDocumentField = (
  fieldOrPayload,
  value,
  declarationIndex,
  partidaIndex,
  documentIndex
) => {
  if (
    typeof fieldOrPayload === 'object' &&
    fieldOrPayload !== null &&
    !Array.isArray(fieldOrPayload)
  ) {
    return {
      type: SET_LAST_EDITED_DOCUMENT_FIELD,
      payload: fieldOrPayload,
    };
  }

  return {
    type: SET_LAST_EDITED_DOCUMENT_FIELD,
    payload: {
      field: fieldOrPayload,
      value,
      declarationIndex,
      partidaIndex,
      documentIndex,
    },
  };
};

export const clearLastEditedDocumentField = () => ({
  type: CLEAR_LAST_EDITED_DOCUMENT_FIELD,
});

export const setSelectedSender = (sender, index = 0) => ({
  type: "SET_SELECTED_SENDER",
  payload: { index, sender },
});

export const setSelectedReceiver = (receiver, index = 0) => ({
  type: "SET_SELECTED_RECEIVER",
  payload: { index, receiver },
});

export const setSelectedAgent = (agent, index = 0) => ({
  type: "SET_SELECTED_AGENT",
  payload: { index, agent },
});

export const resetSelectedSender = () => ({
  type: "RESET_SELECTED_SENDER",
});

export const resetSelectedReceiver = () => ({
  type: "RESET_SELECTED_RECEIVER",
});

export const resetSelectedAgent = () => ({
  type: "RESET_SELECTED_AGENT",
});

// Add new validation errors reducer
const initialValidationErrorsState = {
  errors: [],
  isSpanish: true
};

const validationErrorsReducer = (state = initialValidationErrorsState, action) => {
  switch (action.type) {
    case "SET_VALIDATION_ERRORS":
      return {
        ...state,
        errors: action.payload.errors,
        isSpanish: action.payload.isSpanish
      };
    case "CLEAR_VALIDATION_ERRORS":
      return {
        ...state,
        errors: [],
        isSpanish: state.isSpanish
      };
    case "SET_VALIDATION_LANGUAGE":
      return {
        ...state,
        isSpanish: action.payload
      };
    default:
      return state;
  }
};

// Taric JSON preparation reducer
const initialPreparedJsonState = {
  cliente: null, // Client/importer/exporter details (backward compatibility)
  clientes: [], // Array of clients, one per export
  destinatario: null, // Receiver details (for exports)
  remitente: null, // Sender/shipper details (backward compatibility)
  remitentes: [], // Array of senders, one per export
  agente: null, // Customs agent details (backward compatibility)
  agentes: [], // Array of agents, one per export
  ocrInputID: "", // OCR Input ID
  taricJsonErrors: null, // Taric JSON validation errors

  // Declaration details
  expediente: null, // File reference
  tipo: null, // Declaration type (64 for imports, 32 for exports)
  aduana_entrada: null, // Customs office of entry (for imports)
  aduana_salida: null, // Customs office of exit (for exports)
  aduana_despacho: null, // Customs office of declaration
  fecha_registro: null, // Registration date

  // For exports
  pais_destino: null, // Country of destination
  pais_exportacion: null, // Country of export
  modo_transporte_frontera: null, // Border transport mode
  modo_transporte_interior: null, // Internal transport mode
  identidad_transporte_frontera: null, // Border transport identity
  pais_transporte_frontera: null, // Border transport country

  // Commercial Information
  tiene_contenedores: null, // Has containers (0/1)
  total_bultos: null, // Total packages
  factura: null, // Invoice number
  fecha_factura: null, // Invoice date
  condiciones_entrega: null, // Delivery terms
  naturaleza_transaccion: null, // Transaction nature

  // Items details
  partidas: [],

  // Declaration collections
  declarations: [],

  // State
  isImport: true, // Default to import, can toggle to export
  isReady: false,
  isSubmitted: false,
  submissionError: null,

  // Final structured JSON for Taric
  taricJson: null,
  
  // Selected exportaciones index for grouped Taric JSON outputs
  selectedExportacionesIndex: null,
  
  // Successful and error expedientes for modal display
  successfulExpedientes: [],
  errorExpedientes: [],
};

// preparedJsonReducer with updated field mapping
const preparedJsonReducer = (state = initialPreparedJsonState, action) => {
  switch (action.type) {
    case "SET_TARIC_JSON_ERRORS":
      const errors = Array.isArray(action.payload?.errors) ? action.payload.errors : [];
      const success = Array.isArray(action.payload?.success) ? action.payload.success : [];
      const hasErrors = errors.length > 0;
      const hasSuccess = success.length > 0;
      
      // Extract indices: success → remove, errors → keep
      const successIndices = new Set(
        success.map(item => item.exportacionesIndex).filter(idx => typeof idx === 'number' && idx >= 0)
      );
      const errorIndices = new Set(
        errors.map(err => err.exportacionesIndex).filter(idx => typeof idx === 'number' && idx >= 0)
      );
      
      // CRITICAL: Always preserve the original errors array - don't filter it out The errors should be stored as-is from the API response
      let filteredState = { ...state, taricJsonErrors: action.payload };
      
      // Filter if there are success indices to remove or error indices to keep
      if ((hasSuccess || hasErrors) && state.declarations?.length > 0) {
        const indicesToKeep = Array.from({ length: state.declarations.length }, (_, i) => i)
          .filter(idx => !successIndices.has(idx));
        
        // Filter declarations, clientes, remitentes, agentes by index
        const filteredDeclarations = state.declarations
          .filter((_, idx) => indicesToKeep.includes(idx))
          .map((decl, newIdx) => ({
            ...decl,
            expediente: `CF${(newIdx + 1).toString().padStart(5, '0')}`
          }));
        
        const filteredClientes = state.clientes?.filter((_, idx) => indicesToKeep.includes(idx)) || [];
        const filteredRemitentes = state.remitentes?.filter((_, idx) => indicesToKeep.includes(idx)) || [];
        const filteredAgentes = state.agentes?.filter((_, idx) => indicesToKeep.includes(idx)) || [];
        
        // Regenerate taricJson from filtered declarations (same logic as GENERATE_TARIC_JSON)
        const userName = state.userData?.email || "default.user";
        const validDeclarationsForJson = filteredDeclarations.filter(d => 
          d !== null && d !== undefined && typeof d === 'object' && Object.keys(d).length > 0
        );
        
        const filteredTaricJson = validDeclarationsForJson.length > 0 ? {
          ocr_input_id: (state.ocrInputID || "").toString(),
          taric: {
            clientes: filteredClientes.filter(c => c !== null && c !== undefined),
            agentes: filteredAgentes.filter(a => a !== null && a !== undefined),
            remitentes: filteredRemitentes.filter(r => r !== null && r !== undefined),
            duas: {
              exportaciones: validDeclarationsForJson.map((declaration, idx) => {
                if (!declaration || typeof declaration !== 'object') return null;
                return {
                  id: idx + 1,
                  version: 2,
                  completo: (() => {
                    const val = declaration.completo;
                    if (Array.isArray(val) || val === null || val === undefined || val === '') return 1;
                    const num = Number(val);
                    return (!isNaN(num) && isFinite(num)) ? num : 1;
                  })(),
                  es_plantilla: (() => {
                    const val = declaration.es_plantilla;
                    if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
                    const num = Number(val);
                    return (!isNaN(num) && isFinite(num)) ? num : 0;
                  })(),
                  pais_ubicacion: declaration.pais_ubicacion || "ES",
                  tipo_ubicacion: declaration.tipo_ubicacion || "B",
                  localizacion: declaration.localizacion || "",
                  divisa: declaration.divisa || "900",
                  cambio_divisa: declaration.cambio_divisa || "1,0",
                  es_simplificado: declaration.es_simplificado || 0,
                  es_recapitulativo: declaration.es_recapitulativo || 0,
                  autorizacion_por_operacion: declaration.autorizacion_por_operacion || 0,
                  estado_pdc: declaration.estado_pdc || 0,
                  e_mail: declaration.e_mail || userName,
                  contacto_telefono: declaration.contacto_telefono || "",
                  remitente_id: declaration.remitente_id || null,
                  cliente_id: declaration.cliente_id || null,
                  divisa_iso: declaration.divisa_iso || "EUR",
                  expediente: declaration.expediente,
                  tipo: declaration.tipo != null ? Number(declaration.tipo) : null,
                  agente_id: declaration.agente_id || null,
                  aduana_salida: declaration.aduana_salida,
                  aduana_despacho: declaration.aduana_despacho,
                  pais_destino: declaration.pais_destino,
                  pais_exportacion: declaration.pais_exportacion,
                  modo_transporte_frontera: declaration.modo_transporte_frontera,
                  modo_transporte_interior: declaration.modo_transporte_interior,
                  identidad_transporte_frontera: declaration.identidad_transporte_frontera,
                  pais_transporte_frontera: declaration.pais_transporte_frontera,
                  tiene_contenedores: declaration.tiene_contenedores,
                  total_bultos: declaration.total_bultos,
                  factura: declaration.factura,
                  fecha_factura: declaration.fecha_factura,
                  condiciones_entrega: declaration.condiciones_entrega,
                  naturaleza_transaccion: declaration.naturaleza_transaccion,
                  declaracion_1: declaration.declaracion_1 || null,
                  declaracion_2: declaration.declaracion_2 || null,
                  declaracion: declaration.declaracion || null,
                  fecha_creacion: declaration.fecha_creacion || null,
                  observaciones: declaration.observaciones || null,
                  estado_edi: declaration.estado_edi || null,
                  mrn: declaration.mrn || null,
                  fecha_operacion: declaration.fecha_operacion || null,
                  clave_declarante: declaration.clave_declarante || null,
                  lugar_entrega: declaration.lugar_entrega || null,
                  codigo_un: declaration.codigo_un || null,
                  tipo_autorizacion_cd: declaration.tipo_autorizacion_cd || null,
                  identificacion_adicional: declaration.identificacion_adicional || null,
                  medio_transporte_frontera: declaration.medio_transporte_frontera || null,
                  documentos_transporte: declaration.documentos_transporte || [],
                  referencias_adicionales: declaration.referencias_adicionales || [],
                  informacion_adicional: declaration.informacion_adicional || [],
                  flag_datos_seguridad: (() => {
                    const val = declaration.flag_datos_seguridad;
                    if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
                    const num = Number(val);
                    return (!isNaN(num) && isFinite(num)) ? num : 0;
                  })(),
                  documentos: (() => {
                    if ('documentos' in declaration) {
                      if (Array.isArray(declaration.documentos)) {
                        return declaration.documentos
                          .filter(doc => doc !== null && doc !== undefined && typeof doc === 'object')
                          .map(doc => ({
                            codigo_documento: doc.codigo_documento || null,
                            tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null,
                            numero: doc.numero || null,
                            fecha: doc.fecha || null,
                          }));
                      }
                      return [];
                    }
                    return [];
                  })(),
                  partidas: (Array.isArray(declaration.partidas) ? declaration.partidas : [])
                    .filter(p => p !== null && p !== undefined && typeof p === 'object'),
                };
              }).filter(exp => exp !== null)
            }
          }
        } : state.taricJson;
        
        // Transform error indices to new positions CRITICAL: Only transform indices if we're actually filtering (indicesToKeep.length < declarations.length) If we're keeping all declarations, don't transform the indices
        const oldToNewMap = new Map(indicesToKeep.map((oldIdx, newIdx) => [oldIdx, newIdx]));
        const transformedErrors = errors.map(err => {
          const originalIndex = err.exportacionesIndex;
          // Only remap if the original index is in the map (meaning it's being kept) If indicesToKeep includes all indices (no filtering), keep original index
          const newIndex = oldToNewMap.has(originalIndex) 
            ? oldToNewMap.get(originalIndex) 
            : originalIndex;
          return {
            ...err,
            exportacionesIndex: newIndex
          };
        });
        
        // CRITICAL: Always preserve errors array - use transformed errors (they maintain the same structure) Ensure we're storing the errors correctly
        const finalTaricJsonErrors = {
          ...action.payload,
          errors: transformedErrors.length > 0 ? transformedErrors : errors  // Fallback to original if transformed is empty
        };
        
        filteredState = {
          ...filteredState,
          taricJsonErrors: finalTaricJsonErrors,
          declarations: filteredDeclarations,
          clientes: filteredClientes,
          remitentes: filteredRemitentes,
          agentes: filteredAgentes,
          taricJson: filteredTaricJson,
          cliente: filteredClientes[0] || null,
          remitente: filteredRemitentes[0] || null,
          agente: filteredAgentes[0] || null,
        };
      } else {
        // If no declarations to filter, still preserve errors as-is This handles the case where errors exist but there are no declarations yet
        filteredState = {
          ...state,
          taricJsonErrors: action.payload  // Preserve original payload with errors intact
        };
      }
      
      return filteredState;

    case "CLEAR_TARIC_JSON_ERRORS":
      return {
        ...state,
        taricJsonErrors: null,
      };

    case "SET_OCR_INPUT_ID":
      return {
        ...state,
        ocrInputID: action.payload,
      };

    case "PREPARE_TARIC_DATA":
      const {
        selectedSenders = [],
        selectedReceivers = [],
        selectedAgents = [],
        userData,
        extractedData,
        company,
      } = action.payload || {};

      // Validate extractedData with fallbacks
      let validExtractedData = null;
      
      if (extractedData) {
        if (Array.isArray(extractedData)) {
          // Filter out null/undefined/empty objects
          const filtered = extractedData.filter(d => 
            d !== null && 
            d !== undefined && 
            typeof d === 'object' && 
            Object.keys(d).length > 0
          );
          if (filtered.length > 0) {
            validExtractedData = filtered;
          }
        } else if (typeof extractedData === 'object' && Object.keys(extractedData).length > 0) {
          validExtractedData = [extractedData];
        }
      }
      
      // Fallback: Use state.extractedData if payload extractedData is invalid
      if (!validExtractedData || validExtractedData.length === 0) {
        console.warn('PREPARE_TARIC_DATA: Invalid extractedData in payload, trying state.extractedData');
        if (state.extractedData) {
          if (Array.isArray(state.extractedData) && state.extractedData.length > 0) {
            validExtractedData = state.extractedData;
          } else if (typeof state.extractedData === 'object' && Object.keys(state.extractedData).length > 0) {
            validExtractedData = [state.extractedData];
          }
        }
      }
      
      // If still no valid data, return current state
      if (!validExtractedData || validExtractedData.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid extractedData available');
        return state;
      }

      // Backward compatibility: if old format is used, convert to arrays
      const senders = Array.isArray(selectedSenders) ? selectedSenders : (action.payload?.selectedSender ? [action.payload.selectedSender] : []);
      const receivers = Array.isArray(selectedReceivers) ? selectedReceivers : (action.payload?.selectedReceiver ? [action.payload.selectedReceiver] : []);
      const agents = Array.isArray(selectedAgents) ? selectedAgents : (action.payload?.selectedAgent ? [action.payload.selectedAgent] : []);

      // Process the declarations data - use validated extractedData
      const declarations = validExtractedData;

      // Generate incremented expediente values starting from CF00001
      const generateExpediente = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };

      // Process declarations with incremented expediente values Use arrays to get sender/receiver/agent for each declaration index Filter out invalid declarations first
      const validDeclarations = declarations.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarations.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid declarations after filtering');
        return state;
      }
      
      const processedDeclarations = validDeclarations.map((declaration, index) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          console.warn(`PREPARE_TARIC_DATA: Invalid declaration at index ${index}`);
          return null;
        }
        
        const selectedSender = senders[index] || null;
        const selectedReceiver = receivers[index] || null;
        const selectedAgent = agents[index] || null;

        // Build cliente, remitente, agente for this declaration
        const remitente = selectedReceiver ? {
          id: selectedReceiver.id,
          nombre: selectedReceiver.nombre,
          eori: selectedReceiver.eori,
          nif: selectedReceiver.nif || "",
          identificacion_fiscal: selectedReceiver.nif || "",
          direccion: selectedReceiver.direccion,
          localidad: selectedReceiver.localidad,
          pais: selectedReceiver.pais,
          codigo_postal_espana: company.codigo_postal_espana || null,
          codigo_postal_cee: company.codigo_postal_cee || null,
          codigo_provincia: company.codigo_provincia || null,
          nombre_provincia: company.nombre_provincia || null,
          prefijo_telefono: company.prefijo_telefono || null,
          numero_telefono: company.numero_telefono || null,
          extension_telefono: company.extension_telefono || null,
          numero_fax: company.numero_fax || null,
          e_mail: company.correo_electronico || null,
          aduana_habitual: company.aduana_habitual || null,
          clave_declarante: company.clave_declarante || null,
          garantia: company.garantia || null,
          garantia_transito: company.garantia_transito || null,
          garantia_cabildo: company.garantia_cabildo || null,
          forma_pago: company.forma_pago || null,
          forma_pago2: company.forma_pago2 || null,
          recargo_equivalencia: company.recargo_equivalencia || null,
          cuenta_contable: company.cuenta_contable || null,
          plazo_cobro: company.plazo_cobro || null,
        } : null;

        const cliente = selectedSender ? {
          id: selectedSender.id,
          nombre: selectedSender.nombre,
          eori: selectedSender.eori,
          nif: selectedSender.nif || "",
          identificacion_fiscal: selectedSender.nif || "",
          direccion: selectedSender.direccion,
          localidad: selectedSender.localidad,
          pais: selectedSender.pais,
          codigo_postal: selectedSender.codigo_postal || null,
          nombre_provincia: selectedSender.nombre_provincia || null,
          prefijo_telefono: selectedSender.prefijo_telefono || null,
          numero_telefono: selectedSender.numero_telefono || null,
          extension_telefono: selectedSender.extension_telefono || null,
          numero_fax: selectedSender.numero_fax || null,
          e_mail: selectedSender.e_mail || null,
          proc_simplificado_canarias: selectedSender.proc_simplificado_canarias || null,
          certificado_oea: selectedSender.certificado_oea || null,
          fecha_certificado_oea: selectedSender.fecha_certificado_oea || null,
        } : null;

        const agente = selectedAgent ? {
          id: selectedAgent.id,
          nombre: selectedAgent.nombre,
          nif: selectedAgent.nif || "",
          eori: selectedAgent.eori,
          direccion: selectedAgent.direccion,
          localidad: selectedAgent.localidad,
          pais: selectedAgent.pais,
          codigo: selectedAgent.codigo || null,
          codigo_postal: selectedAgent.codigo_postal || null,
          codigo_provincia: selectedAgent.codigo_provincia || null,
          prefijo_telefono: selectedAgent.prefijo_telefono || null,
          numero_telefono: selectedAgent.numero_telefono || null,
          extension_telefono: selectedAgent.extension_telefono || null,
          numero_fax: selectedAgent.numero_fax || null,
          e_mail: selectedAgent.e_mail || null,
          aduana: selectedAgent.aduana || null,
          clave_declarante: selectedAgent.clave_declarante || null,
          codigo_expedidor_autorizado: selectedAgent.codigo_expedidor_autorizado || null,
          garantia: selectedAgent.garantia || null,
          garantia_cabildo: selectedAgent.garantia_cabildo || null,
        } : null;

        return {
          circuito: 1,
          expediente: declaration.expediente || generateExpediente(index), // Use existing expediente if available, otherwise generate
          tipo: declaration.tipo != null ? Number(declaration.tipo) : 64,
          cliente_id: cliente?.id || null,
          agente_id: agente?.id || null,
          remitente_id: remitente?.id || null,
          _cliente: cliente,
          _remitente: remitente,
          _agente: agente,
          aduana_entrada: declaration.aduana_entrada || null,
          aduana_salida: declaration.aduana_salida || null,
          aduana_despacho: declaration.aduana_despacho || null,
          fecha_registro: declaration.fecha_registro || null,

          // Export-specific fields from the Taric JSON example
          pais_destino: declaration.pais_destino || null,
          pais_exportacion: declaration.pais_exportacion || null,
          modo_transporte_frontera:
            declaration.modo_transporte_frontera || null,
          modo_transporte_interior:
            declaration.modo_transporte_interior || null,
          identidad_transporte_frontera:
            declaration.identidad_transporte_frontera || null,
          pais_transporte_frontera:
            declaration.pais_transporte_frontera || null,

          // Commercial information fields
          tiene_contenedores: declaration.tiene_contenedores || null,
          total_bultos: declaration.total_bultos || null,
          factura: declaration.factura || null,
          fecha_factura: declaration.fecha_factura || null,
          condiciones_entrega: declaration.condiciones_entrega || null,
          naturaleza_transaccion: declaration.naturaleza_transaccion || null,

          // Additional fields
          declaracion_1: declaration.declaracion_1 || null,
          declaracion_2: declaration.declaracion_2 || null,
          declaracion: declaration.declaracion || null,
          fecha_registro: declaration.fecha_registro || null,
          fecha_creacion: declaration.fecha_creacion || null,
          observaciones: declaration.observaciones || null,
          estado_edi: declaration.estado_edi || null,
          // circuito: declaration.circuito || null,
          atr1: declaration.atr1 || null,
          eur1: declaration.eur1 || null,
          dv1: declaration.dv1 || null,

          // New declaration-level fields Validate numeric fields - ensure they're not arrays
          completo: (() => {
            const val = declaration.completo;
            if (Array.isArray(val) || val === null || val === undefined || val === '') return 1;
            const num = Number(val);
            return (!isNaN(num) && isFinite(num)) ? num : 1;
          })(),
          version: declaration.version ?? 2,
          // nombre_usuario_ultima_modificacion: declaration.nombre_usuario_ultima_modificacion ?? "",
          es_plantilla: (() => {
            const val = declaration.es_plantilla;
            if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
            const num = Number(val);
            return (!isNaN(num) && isFinite(num)) ? num : 0;
          })(),
          pais_ubicacion: declaration.pais_ubicacion ?? "ES",
          tipo_ubicacion: declaration.tipo_ubicacion ?? "B",
          localizacion: declaration.localizacion ?? "",
          divisa: declaration.divisa ?? "900",
          cambio_divisa: declaration.cambio_divisa ?? "1,0",
          es_simplificado: declaration.es_simplificado ?? 0,
          es_recapitulativo: declaration.es_recapitulativo ?? 0,
          autorizacion_por_operacion: declaration.autorizacion_por_operacion ?? 0,
          estado_pdc: declaration.estado_pdc ?? 0,
          e_mail: declaration.e_mail ?? "",
          contacto_telefono: declaration.contacto_telefono ?? "",
          divisa_iso: declaration.divisa_iso ?? "EUR",

          // Missing fields from PDF analysis (Page 1 - Expediente Tab)
          mrn: declaration.mrn || null, // Movement Reference Number - CRITICAL missing field
          fecha_operacion: declaration.fecha_operacion || null, // Operation Date
          clave_declarante: declaration.clave_declarante || company.clave_declarante || null, // Declarant Key (from declaration or company)

          // Missing fields from PDF analysis (Page 2 - Declaración Tab)
          lugar_entrega: declaration.lugar_entrega || null, // Delivery Place
          codigo_un: declaration.codigo_un || null, // UN Code for delivery place
          tipo_autorizacion_cd: declaration.tipo_autorizacion_cd || null, // Type under 49 Autorización C.D.
          identificacion_adicional: declaration.identificacion_adicional || null, // Additional Identification

          // Missing fields from PDF analysis (Page 3 - Datos Transporte Tab)
          medio_transporte_frontera: declaration.medio_transporte_frontera || null, // Transport Means (separate from modo_transporte_frontera)

          // Missing fields from PDF analysis (Page 4 - Documentos/Autoriz. Tab)
          documentos_transporte: declaration.documentos_transporte || [], // Transport Documents (N730, etc.) - declaration level

          // Missing fields from PDF analysis (Page 5 - Información Adicional Tab)
          referencias_adicionales: declaration.referencias_adicionales || [], // Additional References (Y024, etc.)
          informacion_adicional: declaration.informacion_adicional || [], // Additional Information (I5007, etc.)

          // Missing fields from PDF analysis (Page 6 - Importes Tab)
          flag_datos_seguridad: (() => {
            const val = declaration.flag_datos_seguridad;
            if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
            const num = Number(val);
            return (!isNaN(num) && isFinite(num)) ? num : 0;
          })(), // Security Data Flag (0 = No contiene datos de seguridad)

          // Declaration-level documentos (root level of expediente) If documentos property exists in declaration (even if empty), use it; otherwise set to empty array NOTE: Do NOT auto-aggregate here - let the component handle aggregation when toggle is enabled
          documentos: (() => {
            // Check if documentos property exists in the declaration object If it exists (even if empty array), respect the user's choice
            if ('documentos' in declaration) {
              if (Array.isArray(declaration.documentos)) {
                // User has explicitly set documentos (could be empty array if toggle is off)
                return declaration.documentos
                  .filter(doc => doc !== null && doc !== undefined && typeof doc === 'object')
                  .map(doc => ({
                    codigo_documento: doc.codigo_documento || null,
                    tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null,
                    numero: doc.numero || null,
                    fecha: doc.fecha || null,
                  }));
              }
              // If documentos exists but is not an array, return empty array
              return [];
            }
            
            // documentos property doesn't exist - set to empty array (don't auto-aggregate) The component will handle aggregation when the toggle is enabled
            return [];
          })(),

          // Items (partidas) - validate and filter
          partidas:
            (declaration.partidas && Array.isArray(declaration.partidas) ? declaration.partidas : [])
              .filter(item => item !== null && item !== undefined && typeof item === 'object')
              .map((item) => {
              // Convert provincia nombre/codigo to canonical codigo if needed
              // NOTE: provincia codes are 2-digit string codes ("01".."52"). Some legacy data may store them as numbers.
              let provinciaOrigen = item.provincia_origen ?? null;
              if (provinciaOrigen !== null && provinciaOrigen !== undefined && provinciaOrigen !== '') {
                const provincias = getProvincias();

                const provinciaKey = (() => {
                  // Handle legacy object shapes (e.g. { codigo, nombre })
                  if (typeof provinciaOrigen === 'object') {
                    if (provinciaOrigen && provinciaOrigen.codigo !== undefined && provinciaOrigen.codigo !== null) {
                      return String(provinciaOrigen.codigo).trim();
                    }
                    if (provinciaOrigen && provinciaOrigen.nombre !== undefined && provinciaOrigen.nombre !== null) {
                      return String(provinciaOrigen.nombre).trim();
                    }
                    return '';
                  }

                  const str = String(provinciaOrigen).trim();
                  // If it's numeric (e.g. 1 or "1"), preserve leading zeros by padding to 2 digits.
                  if (/^\d+$/.test(str)) {
                    return str.padStart(2, '0');
                  }
                  return str;
                })();

                if (provinciaKey) {
                  const provinciaKeyLower = provinciaKey.toLowerCase();
                  const foundProvincia = provincias.find(
                    (p) =>
                      p.codigo === provinciaKey ||
                      p.nombre === provinciaKey ||
                      (p.nombre?.toLowerCase() === provinciaKeyLower)
                  );

                  // Store canonical code if we can resolve it; otherwise, at least store the normalized string key.
                  provinciaOrigen = foundProvincia ? foundProvincia.codigo : provinciaKey;
                }
              }
              
              // Validate item structure
              if (!item || typeof item !== 'object') {
                console.warn('PREPARE_TARIC_DATA: Invalid item in partidas, skipping');
                return null;
              }
              
              return {
                codigo_nc: item.codigo_nc || null,
                descripcion_mercancia:
                  item.descripcion_mercancia || item.descripcion || "",
                masa_bruta: item.masa_bruta || null,
                masa_neta: item.masa_neta || null,
                pais_origen: item.pais_origen || remitente?.pais || "ES",
                provincia_origen: provinciaOrigen,

              // Financial information
              precio: item.precio || null,
              cantidad_euros: item.cantidad_euros || null,
              valor_estadistico: item.valor_estadistico || null,
              cantidad_divisas: item.cantidad_divisas || null,

              // Tariff information
              regimen_solicitado_precedente:
                item.regimen_solicitado_precedente ||
                item.regimen_aduanero ||
                "10.00",
              regimen_comunitario_nacional:
                item.regimen_comunitario_nacional !== undefined && item.regimen_comunitario_nacional !== null
                  ? item.regimen_comunitario_nacional
                  : null, // Explicit null when empty (not undefined)

              // Missing fields from PDF analysis (Page 9-13 - Partida Details)
              observaciones: item.observaciones || null, // Observations per partida (specific text like "SOIVRE\nEFITCAN")

              // New fields from table
              adiciones: item.adiciones || "0",
              deducciones: item.deducciones || "0",
              observaciones_arancelarias: item.observaciones_arancelarias || 512,
              observaciones_arancelarias_2: item.observaciones_arancelarias_2 || 0,
              deducciones_base_iva: item.deducciones_base_iva || "0",

              // Documents and packaging from the Taric JSON example - validate arrays
              documentos: (Array.isArray(item.documentos) ? item.documentos : [])
                .filter(doc => doc !== null && doc !== undefined)
                .map(doc => ({
                  ...doc,
                  tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null
                })),
              embalajes: (Array.isArray(item.embalajes) ? item.embalajes : []).map(embalaje => {
                // Ensure cantidad_restante is an integer
                if (embalaje && 'cantidad_restante' in embalaje) {
                  const cantidadRestante = embalaje.cantidad_restante;
                  let cantidadRestanteInt = null;
                  if (cantidadRestante !== null && cantidadRestante !== undefined && cantidadRestante !== '') {
                    const parsed = parseInt(String(cantidadRestante).trim(), 10);
                    if (!isNaN(parsed) && isFinite(parsed)) {
                      cantidadRestanteInt = parsed;
                    }
                  }
                  return {
                    ...embalaje,
                    cantidad_restante: cantidadRestanteInt
                  };
                }
                return embalaje;
              }),
              };
            }).filter(item => item !== null), // Remove any null entries from invalid items
        };
      }).filter(decl => decl !== null); // Remove any null entries from invalid declarations
      
      // Final validation: ensure we have valid processed declarations
      if (!processedDeclarations || processedDeclarations.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid processed declarations after processing');
        return state;
      }

      // Extract clientes, remitentes, agentes from all declarations (one per export, allowing duplicates)
      const allClientes = processedDeclarations.map(d => d?._cliente).filter(Boolean);
      const allRemitentes = processedDeclarations.map(d => d?._remitente).filter(Boolean);
      const allAgentes = processedDeclarations.map(d => d?._agente).filter(Boolean);
  
      // Clean up temporary fields
      const cleanedDeclarations = processedDeclarations.map(({ _cliente, _remitente, _agente, ...decl }) => decl);

      return {
        ...state,
        cliente: allClientes.length > 0 ? allClientes[0] : null, // Keep first for backward compatibility
        remitente: allRemitentes.length > 0 ? allRemitentes[0] : null,
        agente: allAgentes.length > 0 ? allAgentes[0] : null,
        clientes: allClientes, // Array with one entry per export (allowing duplicates)
        remitentes: allRemitentes, // Array with one entry per export (allowing duplicates)
        agentes: allAgentes, // Array with one entry per export (allowing duplicates)
        userData,
        declarations: cleanedDeclarations,
        editableData: cleanedDeclarations,
        isReady: true,
      };

    case "GENERATE_TARIC_JSON":
      // Validate state before generating JSON
      if (!state.declarations || !Array.isArray(state.declarations) || state.declarations.length === 0) {
        console.error('GENERATE_TARIC_JSON: No valid declarations available');
        return state;
      }
      
      const userName =
        state.userData
          ? `${state.userData?.email}`
          : "default.user";
          
      // Validate and filter declarations
      const validDeclarationsForJson = state.declarations.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarationsForJson.length === 0) {
        console.error('GENERATE_TARIC_JSON: No valid declarations after filtering');
        return state;
      }

      const taricJson = {
        ocr_input_id: (state.ocrInputID || "").toString(),
        taric: {
          // Use arrays if available, otherwise fall back to single values for backward compatibility
          clientes: (state.clientes && Array.isArray(state.clientes) && state.clientes.length > 0) 
            ? state.clientes.filter(c => c !== null && c !== undefined)
            : (state.cliente && typeof state.cliente === 'object' ? [state.cliente] : []),
          agentes: (state.agentes && Array.isArray(state.agentes) && state.agentes.length > 0) 
            ? state.agentes.filter(a => a !== null && a !== undefined)
            : (state.agente && typeof state.agente === 'object' ? [state.agente] : []),
          remitentes: (state.remitentes && Array.isArray(state.remitentes) && state.remitentes.length > 0) 
            ? state.remitentes.filter(r => r !== null && r !== undefined)
            : (state.remitente && typeof state.remitente === 'object' ? [state.remitente] : []),
          duas: {
            exportaciones:
              validDeclarationsForJson.map((declaration, idx) => {
                // Validate declaration before processing
                if (!declaration || typeof declaration !== 'object') {
                  console.warn(`GENERATE_TARIC_JSON: Invalid declaration at index ${idx}, skipping`);
                  return null;
                }
                
                return {
                id: idx + 1, // Unique, incrementing integer
                version: 2,
                // Validate numeric fields - ensure they're not arrays
                completo: (() => {
                  const val = declaration.completo;
                  if (Array.isArray(val) || val === null || val === undefined || val === '') return 1;
                  const num = Number(val);
                  return (!isNaN(num) && isFinite(num)) ? num : 1;
                })(),
                // nombre_usuario_ultima_modificacion: userName,
                es_plantilla: (() => {
                  const val = declaration.es_plantilla;
                  if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
                  const num = Number(val);
                  return (!isNaN(num) && isFinite(num)) ? num : 0;
                })(),
                pais_ubicacion: declaration.pais_ubicacion || "ES",
                tipo_ubicacion: declaration.tipo_ubicacion || "B",
                localizacion: declaration.localizacion || "",
                divisa: declaration.divisa || "900",
                cambio_divisa: declaration.cambio_divisa || "1,0",
                es_simplificado: declaration.es_simplificado || 0,
                es_recapitulativo: declaration.es_recapitulativo || 0,
                autorizacion_por_operacion: declaration.autorizacion_por_operacion || 0,
                estado_pdc: declaration.estado_pdc || 0,
                e_mail: declaration.e_mail || userName,
                contacto_telefono: declaration.contacto_telefono || "",
                remitente_id: declaration.remitente_id || null,
                cliente_id: declaration.cliente_id || null,
                divisa_iso: declaration.divisa_iso || "EUR",
                // Basic Declaration Information
                expediente: declaration.expediente,
                tipo: declaration.tipo != null ? Number(declaration.tipo) : null,
                remitente_id: declaration.remitente_id || null,
                cliente_id: declaration.cliente_id || null,
                agente_id: declaration.agente_id || null,
                // Customs and Transport Information
                aduana_salida: declaration.aduana_salida,
                aduana_despacho: declaration.aduana_despacho,
                pais_destino: declaration.pais_destino,
                pais_exportacion: declaration.pais_exportacion,
                modo_transporte_frontera: declaration.modo_transporte_frontera,
                modo_transporte_interior: declaration.modo_transporte_interior,
                identidad_transporte_frontera:
                  declaration.identidad_transporte_frontera,
                pais_transporte_frontera: declaration.pais_transporte_frontera,
                // Commercial information
                tiene_contenedores: declaration.tiene_contenedores,
                total_bultos: declaration.total_bultos,
                factura: declaration.factura,
                fecha_factura: declaration.fecha_factura,
                condiciones_entrega: declaration.condiciones_entrega,
                naturaleza_transaccion: declaration.naturaleza_transaccion,
                // Additional fields
                declaracion_1: declaration.declaracion_1 || null,
                declaracion_2: declaration.declaracion_2 || null,
                declaracion: declaration.declaracion || null,
                //fecha_registro: declaration.fecha_registro || null,
                fecha_creacion: declaration.fecha_creacion || null,
                observaciones: declaration.observaciones || null,
                estado_edi: declaration.estado_edi || null,
                //circuito: declaration.circuito || null,

                // Missing fields from PDF analysis - Declaration level
                mrn: declaration.mrn || null, // Movement Reference Number - CRITICAL
                fecha_operacion: declaration.fecha_operacion || null, // Operation Date
                clave_declarante: declaration.clave_declarante || null, // Declarant Key
                lugar_entrega: declaration.lugar_entrega || null, // Delivery Place
                codigo_un: declaration.codigo_un || null, // UN Code for delivery place
                tipo_autorizacion_cd: declaration.tipo_autorizacion_cd || null, // Type under 49 Autorización C.D.
                identificacion_adicional: declaration.identificacion_adicional || null, // Additional Identification
                medio_transporte_frontera: declaration.medio_transporte_frontera || null, // Transport Means
                documentos_transporte: declaration.documentos_transporte || [], // Transport Documents (N730, etc.)
                referencias_adicionales: declaration.referencias_adicionales || [], // Additional References (Y024, etc.)
                informacion_adicional: declaration.informacion_adicional || [], // Additional Information (I5007, etc.)
                flag_datos_seguridad: (() => {
                  const val = declaration.flag_datos_seguridad;
                  if (Array.isArray(val) || val === null || val === undefined || val === '') return 0;
                  const num = Number(val);
                  return (!isNaN(num) && isFinite(num)) ? num : 0;
                })(), // Security Data Flag

                // Declaration-level documentos (root level of expediente) If documentos property exists in declaration (even if empty), use it; otherwise set to empty array NOTE: Do NOT auto-aggregate here - let the component handle aggregation when toggle is enabled
                documentos: (() => {
                  // Check if documentos property exists in the declaration object If it exists (even if empty array), respect the user's choice
                  if ('documentos' in declaration) {
                    if (Array.isArray(declaration.documentos)) {
                      // User has explicitly set documentos (could be empty array if toggle is off)
                      return declaration.documentos
                        .filter(doc => doc !== null && doc !== undefined && typeof doc === 'object')
                        .map(doc => ({
                          codigo_documento: doc.codigo_documento || null,
                          tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null,
                          numero: doc.numero || null,
                          fecha: doc.fecha || null,
                        }));
                    }
                    // If documentos exists but is not an array, return empty array
                    return [];
                  }
                  
                  // documentos property doesn't exist - set to empty array (don't auto-aggregate) The component will handle aggregation when the toggle is enabled
                  return [];
                })(),

                // Items list - validate and filter, ensuring cantidad_restante is an integer
                partidas: (Array.isArray(declaration.partidas) ? declaration.partidas : [])
                  .filter(p => p !== null && p !== undefined && typeof p === 'object')
                  .map(partida => {
                    // Ensure embalajes have cantidad_restante as integer
                    if (Array.isArray(partida.embalajes)) {
                      return {
                        ...partida,
                        embalajes: partida.embalajes.map(embalaje => {
                          if (embalaje && 'cantidad_restante' in embalaje) {
                            const cantidadRestante = embalaje.cantidad_restante;
                            // Convert to integer if it's a string or number, otherwise set to null
                            let cantidadRestanteInt = null;
                            if (cantidadRestante !== null && cantidadRestante !== undefined && cantidadRestante !== '') {
                              const parsed = parseInt(String(cantidadRestante).trim(), 10);
                              if (!isNaN(parsed) && isFinite(parsed)) {
                                cantidadRestanteInt = parsed;
                              }
                            }
                            return {
                              ...embalaje,
                              cantidad_restante: cantidadRestanteInt
                            };
                          }
                          return embalaje;
                        })
                      };
                    }
                    return partida;
                  }),
              };
              }).filter(exp => exp !== null), // Remove any null entries from invalid declarations
          },
        },
      };

      return {
        ...state,
        taricJson,
      };

    case "SET_SUBMISSION_STATUS":
      return {
        ...state,
        isSubmitted: action.payload.isSubmitted,
        submissionError: action.payload.submissionError,
      };

    case "RESET_PREPARED_JSON":
      return initialPreparedJsonState;

    case "SET_TARIC_JSON_FROM_GROUPED":
      // Set taricJson directly from grouped API response
      return {
        ...state,
        taricJson: action.payload.taricJson,
        ocrInputID: action.payload.ocr_input_id || "",
        selectedExportacionesIndex: action.payload.exportacionesIndex || null,
      };
    
    case "SET_SELECTED_EXPORTACIONES_INDEX":
      return {
        ...state,
        selectedExportacionesIndex: action.payload,
      };

    case "SET_SUCCESSFUL_EXPEDIENTES":
      return {
        ...state,
        successfulExpedientes: action.payload,
      };

    case "SET_ERROR_EXPEDIENTES":
      return {
        ...state,
        errorExpedientes: action.payload,
      };

    case "CLEAR_EXPEDIENTES":
      return {
        ...state,
        successfulExpedientes: [],
        errorExpedientes: [],
      };

    default:
      return state;
  }
};

export const SET_COMBINED_DOCUMENT = "SET_COMBINED_DOCUMENT";

// Action Creators
export const setCombinedDocument = (isCombined) => ({
  type: SET_COMBINED_DOCUMENT,
  payload: isCombined,
});

// Action creators for prepared JSON
export const prepareTaricData = (data) => ({
  type: "PREPARE_TARIC_DATA",
  payload: data,
});

export const setTaricJsonErrors = (errors) => ({
  type: "SET_TARIC_JSON_ERRORS",
  payload: errors,
});

export const setSuccessfulExpedientes = (expedientes) => ({
  type: "SET_SUCCESSFUL_EXPEDIENTES",
  payload: expedientes,
});

export const setErrorExpedientes = (expedientes) => ({
  type: "SET_ERROR_EXPEDIENTES",
  payload: expedientes,
});

export const clearExpedientes = () => ({
  type: "CLEAR_EXPEDIENTES",
});

export const filterDeclarationsByErrors = (errorIndices) => ({
  type: "FILTER_DECLARATIONS_BY_ERRORS",
  payload: errorIndices,
});

export const clearTaricJsonErrors = () => ({
  type: "CLEAR_TARIC_JSON_ERRORS",
});

export const generateTaricJson = () => ({
  type: "GENERATE_TARIC_JSON",
});

export const setJsonSubmissionStatus = (status) => ({
  type: "SET_SUBMISSION_STATUS",
  payload: status,
});

export const resetPreparedJson = () => ({
  type: "RESET_PREPARED_JSON",
});

export const setOcrInputID = (id) => ({
  type: "SET_OCR_INPUT_ID",
  payload: id,
});

export const setSelectedExportacionesIndex = (index) => ({
  type: "SET_SELECTED_EXPORTACIONES_INDEX",
  payload: index,
});

// Action Creator
export const toggleCollapseFiles = () => ({
  type: TOGGLE_COLLAPSE_FILES,
});

// Add new action creators for validation errors
export const setValidationErrors = (errors, isSpanish) => ({
  type: "SET_VALIDATION_ERRORS",
  payload: { errors, isSpanish }
});

export const clearValidationErrors = () => ({
  type: "CLEAR_VALIDATION_ERRORS"
});

export const setValidationLanguage = (isSpanish) => ({
  type: "SET_VALIDATION_LANGUAGE",
  payload: isSpanish
});

// Add selected declaration reducer
const selectedDeclarationReducer = (state = null, action) => {
  switch (action.type) {
    case SET_SELECTED_DECLARATION:
      return action.payload;
    case CLEAR_SELECTED_DECLARATION:
      return null;
    default:
      return state;
  }
};

// Add action creators for selected declaration
export const setSelectedDeclaration = (declaration) => ({
  type: SET_SELECTED_DECLARATION,
  payload: declaration,
});

export const clearSelectedDeclaration = () => ({
  type: CLEAR_SELECTED_DECLARATION,
});

const initialCreditStatsState = {
  current: null,
  usage: null,
  exportCost: null,
  loading: false,
  error: null,
};

// Credit stats reducer
const creditStatsReducer = (state = initialCreditStatsState, action) => {
  switch (action.type) {
    case 'SET_CREDIT_STATS':
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };
    case 'SET_CREDIT_STATS_LOADING':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'SET_CREDIT_STATS_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'CLEAR_CREDIT_STATS':
      return initialCreditStatsState;
    default:
      return state;
  }
};

// Credit stats actions
export const setCreditStats = (data) => ({
  type: 'SET_CREDIT_STATS',
  payload: data,
});

export const setCreditStatsLoading = () => ({
  type: 'SET_CREDIT_STATS_LOADING',
});

export const setCreditStatsError = (error) => ({
  type: 'SET_CREDIT_STATS_ERROR',
  payload: error,
});

export const clearCreditStats = () => ({
  type: 'CLEAR_CREDIT_STATS',
});

// Thunk to fetch credit stats
export const fetchCreditStats = () => async (dispatch) => {
  dispatch(setCreditStatsLoading());
  try {
    const API_BASE_URL = process.env.REACT_APP_DOCS_API_KEY;
    // Import apiFetch dynamically to avoid circular dependencies
    const { default: apiFetch } = await import('../../utils/api');
    const [currentRes, usageRes, exportCostRes] = await Promise.all([
      apiFetch(`https://${API_BASE_URL}/api/credits/current`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      }),
      apiFetch(`https://${API_BASE_URL}/api/credits/usage/today`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      }),
      apiFetch(`https://${API_BASE_URL}/api/credits/export-cost`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      })
    ]);
    
    if (!currentRes.ok || !usageRes.ok || !exportCostRes.ok) {
      const errorDetails = {
        current: currentRes.status,
        usage: usageRes.status,
        exportCost: exportCostRes.status
      };
      throw new Error(`Failed to fetch credit stats: ${JSON.stringify(errorDetails)}`);
    }
    
    const [current, usage, exportCost] = await Promise.all([
      currentRes.json(),
      usageRes.json(),
      exportCostRes.json()
    ]);
    
    dispatch(setCreditStats({ current, usage, exportCost }));
  } catch (err) {
    dispatch(setCreditStatsError(err.message));
  }
};

// Stripe credit products
const initialProductsState = [
  {
    credits: "10,000",
    price: "€499.00",
    productId: "prod_10000_credits"
  },
  {
    credits: "5,000",
    price: "€299.00",
    productId: "prod_5000_credits"
  },
  {
    credits: "1,000",
    price: "€59.00",
    productId: "prod_1000_credits"
  },
  {
    credits: "100",
    price: "€9.99",
    productId: "prod_100_credits"
  },
];

export const productsReducer = (state = initialProductsState, action) => {
  // This is a static list, but you could add actions to update if needed
  return state;
};

// Suggestions reducer
const initialSuggestionsState = {
  suggestions: [], // Always use array, never null
  suggestionJobId: null,
};

export const suggestionsReducer = (state = initialSuggestionsState, action) => {
  switch (action.type) {
    case "SET_SUGGESTIONS":
      // Validate payload
      if (!action.payload) {
        console.warn('SET_SUGGESTIONS: Invalid payload (null/undefined)');
        return state;
      }
      
      // Validate and normalize suggestions
      let validSuggestions = null;
      const { suggestions, suggestionJobId } = action.payload;
      
      if (suggestions !== null && suggestions !== undefined) {
        if (Array.isArray(suggestions)) {
          // Filter out null/undefined/empty objects
          const filtered = suggestions.filter(s => 
            s !== null && 
            s !== undefined && 
            typeof s === 'object' && 
            (s.exportacion_index !== undefined || s.agents || s.senders || s.receivers)
          );
          validSuggestions = filtered.length > 0 ? filtered : [];
        } else if (typeof suggestions === 'object') {
          // Single suggestion object, convert to array
          validSuggestions = [suggestions];
        } else {
          console.warn('SET_SUGGESTIONS: Invalid suggestions type:', typeof suggestions);
          validSuggestions = [];
        }
      } else {
        // null or undefined - store as empty array for consistency
        validSuggestions = [];
      }
      
      
      return {
        suggestions: validSuggestions,
        suggestionJobId: suggestionJobId || null,
      };
    case "CLEAR_SUGGESTIONS":
      return {
        suggestions: [], // Always return array
        suggestionJobId: null,
      };
    default:
      return state;
  }
};

// Action creators for suggestions
export const SET_SUGGESTIONS = "SET_SUGGESTIONS";
export const CLEAR_SUGGESTIONS = "CLEAR_SUGGESTIONS";

export const setSuggestions = (suggestions, suggestionJobId) => ({
  type: SET_SUGGESTIONS,
  payload: { suggestions, suggestionJobId },
});

export const clearSuggestions = () => ({
  type: CLEAR_SUGGESTIONS,
});

// Templates reducer with API persistence
const API_BASE_URL = process.env.REACT_APP_DOCS_API_KEY;
const TEMPLATES_API_ENDPOINT = `https://${API_BASE_URL}/api/templates`;
const ADDITIONAL_CONTEXT_PRESETS_API_ENDPOINT = `https://${API_BASE_URL}/api/items/additional_context_presets`;

// Default templates - extracted to avoid duplication
const getDefaultTemplates = () => [
    {
      id: 'template-1',
      title: 'Template 1',
      color: '#2DD0D6',
      data: {
        regimen_solicitud: '10',
        declaracion_1: 'EX',
        declaracion_2: 'A',
        naturaleza_transaccion: '11',
        medio_transporte_frontera: '30',
        embalajes: [
          {
            marcas: [{ marca: 'TEST' }],
          },
        ],
      },
    },
    {
      id: 'template-2',
      title: 'Template 2',
      color: '#10B981',
      data: {
        aduana_despacho: '0441',
        pais_exportacion: 'ES',
        provincia: 'AL',
        localizacion_mercancia: '0441LAME05',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'Y024', tipo: 3 },
        ],
      },
    },
    {
      id: 'template-3',
      title: 'Template 3',
      color: '#F59E0B',
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'N003', tipo: 2 },
        ],
      },
    },
  ];

// Clean up templates: remove marcas from declaration level (should only be in embalajes)
const cleanTemplates = (templates) => {
  if (!Array.isArray(templates)) {
    return templates;
  }
  
  return templates.map(template => {
    if (!template || typeof template !== 'object' || !template.data) {
      return template;
    }
    
    // Create a copy to avoid mutating the original
    const cleanedTemplate = { ...template };
    cleanedTemplate.data = { ...template.data };
    
    // Remove marcas from declaration level if it exists marcas should only be in embalajes, not at the declaration level
    if ('marcas' in cleanedTemplate.data) {
      delete cleanedTemplate.data.marcas;
    }
    
    return cleanedTemplate;
  });
};

// Load templates from API
const loadTemplatesFromAPI = async () => {
  try {
    const response = await apiFetch(TEMPLATES_API_ENDPOINT, {
      cache: 'no-cache', // Bypass browser cache to always get fresh data
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.warn('Failed to fetch templates from API, using defaults');
      return getDefaultTemplates();
    }

    const result = await response.json();
    
    // Handle the API response structure: { success: true, data: [...] } API returns: { id: 12, data: { id: "almeria", title: "...", data: {...} } } We need to merge outer numeric id with inner data object
    let rawTemplates = [];
    if (result && result.success && Array.isArray(result.data)) {
      rawTemplates = result.data;
    } else if (Array.isArray(result)) {
      // Fallback: if response is directly an array
      rawTemplates = result;
    } else if (result && Array.isArray(result.data)) {
      rawTemplates = result.data;
    }

    // Transform API response: merge outer numeric id (12) with inner data object API structure can be: 1. { id: 12, data: { title: "...", color: "...", data: {...} } } (normal) 2. { id: 12, data: { data: { id: 12, title: "...", color: "...", data: {...} } } } (nested - Almeria) Transformed: { id: 12, title: "...", color: "...", data: {...} }
    const templates = rawTemplates
      .filter(t => t && typeof t === 'object' && t.id && t.data)
      .map(t => {
        // Handle nested data structure (Almeria case: t.data.data has title/color) Check if t.data.data exists AND has title/color properties (nested structure) Otherwise use direct structure (t.data)
        const hasNestedStructure = t.data && 
          typeof t.data === 'object' && 
          t.data.data && 
          typeof t.data.data === 'object' &&
          'title' in t.data.data &&
          'color' in t.data.data;
        
        const innerData = hasNestedStructure
          ? t.data.data  // Nested structure: use t.data.data (Almeria)
          : t.data;      // Direct structure: use t.data (others)
        
        // Extract inner data, but exclude the inner id (if it exists) to preserve outer numeric id
        const { id: innerId, ...innerDataWithoutId } = innerData;
        // Use outer numeric id (12, 13, etc.) as the main id, merge with inner data
        const transformed = {
          id: t.id, // Use outer numeric id (12, 13, etc.) for API operations
          ...innerDataWithoutId, // Spread inner data (title, color, data, etc.) without inner id
        };
        
        // Debug logging
        if (!transformed.title || !transformed.color) {
          console.warn('Template transformation issue:', {
            originalId: t.id,
            hasNestedStructure,
            innerDataKeys: Object.keys(innerData || {}),
            transformedKeys: Object.keys(transformed),
            innerData,
            transformed
          });
        }
        
        return transformed;
      });

    // Validate each template has required fields (accept both numeric and string IDs)
    const validTemplates = templates.filter(t => 
      t && 
      typeof t === 'object' && 
      t.id && 
      (typeof t.id === 'string' || typeof t.id === 'number') &&
      t.title &&
      typeof t.title === 'string'
    );

    if (validTemplates.length > 0) {
      // Clean up templates: remove marcas from declaration level
      const cleanedTemplates = cleanTemplates(validTemplates);
      return cleanedTemplates;
    } else {
      console.warn('No valid templates found in API response, using defaults');
      return getDefaultTemplates();
    }
  } catch (error) {
    console.error('Error loading templates from API:', error);
    // Default templates if API call fails
    return getDefaultTemplates();
  }
};

// Save template to API (for add/update operations)
const saveTemplateToAPI = async (template, method = 'POST') => {
  try {
    // Validate template before saving
    if (!template || typeof template !== 'object') {
      console.error('Cannot save template: invalid template object');
      return null;
    }
    
    // Accept both numeric (from API) and string (from defaults) IDs
    if (!template.id || (typeof template.id !== 'string' && typeof template.id !== 'number')) {
      console.error('Cannot save template: missing valid id');
      return null;
    }
    
    if (!template.title || typeof template.title !== 'string') {
      console.error('Cannot save template: missing valid title');
      return null;
    }
    
    // For PUT (update), API requires numeric ID - reject string IDs (default templates)
    if (method === 'PUT' && typeof template.id === 'string') {
      const error = 'Cannot update default template. Default templates are read-only. Please create a new template or wait for templates to load from the server.';
      console.error(error);
      throw new Error(error);
    }
    
    // Clean up template: remove marcas from declaration level before saving But preserve title, color, and id at the root level
    const cleanedTemplate = cleanTemplates([template])[0];
    
    // Ensure cleaned template has required fields (cleanTemplates might not preserve them)
    if (!cleanedTemplate.title && template.title) {
      cleanedTemplate.title = template.title;
    }
    if (!cleanedTemplate.color && template.color) {
      cleanedTemplate.color = template.color;
    }
    if (!cleanedTemplate.id && template.id) {
      cleanedTemplate.id = template.id;
    }
    
    // For PUT (update), use the numeric id in the URL (e.g., /templates/12) For POST (create), send to base endpoint
    const url = method === 'PUT' 
      ? `${TEMPLATES_API_ENDPOINT}/${cleanedTemplate.id}`
      : TEMPLATES_API_ENDPOINT;
    
    // Prepare request body: API expects { data: { id, title, color, data: {...} } } Extract the inner data structure (id, title, color, data) for the API Ensure title and color are valid strings
    const templateTitle = (cleanedTemplate.title && typeof cleanedTemplate.title === 'string') 
      ? cleanedTemplate.title.trim() 
      : (template.title && typeof template.title === 'string' ? template.title.trim() : '');
    const templateColor = (cleanedTemplate.color && typeof cleanedTemplate.color === 'string') 
      ? cleanedTemplate.color.trim() 
      : (template.color && typeof template.color === 'string' ? template.color.trim() : '#6B7280');
    
    // Validate title is not empty
    if (!templateTitle) {
      console.error('Cannot save template: title is required and cannot be empty', { template, cleanedTemplate });
      throw new Error('Template title is required');
    }
    
    // Validate color is a valid hex color
    if (!/^#[0-9A-Fa-f]{6}$/.test(templateColor)) {
      console.error('Cannot save template: color must be a valid hex color code', { templateColor, template, cleanedTemplate });
      throw new Error('Template color must be a valid hex color code (e.g., #2DD0D6)');
    }
    
    // Based on API schema example, POST expects root-level structure: { title, color, data: {...} } GET response has nested structure: { id: 12, data: { title, color, data: {...} } } But POST/PUT requests should be at root level without the outer "data" wrapper Ensure all values are properly stringified and not undefined/null
    const requestBody = {
      title: String(templateTitle || '').trim(),
      color: String(templateColor || '#6B7280').trim(),
      data: cleanedTemplate.data && typeof cleanedTemplate.data === 'object' ? cleanedTemplate.data : {}
    };
    
    // For POST, optionally include id if provided (API may use it or generate a new one) For PUT, include inner id if it exists (like "almeria" for updates)
    if (method === 'POST' && cleanedTemplate.data?.id) {
      // Include inner id for POST if it exists (like "almeria")
      requestBody.id = String(cleanedTemplate.data.id);
    } else if (method === 'PUT' && cleanedTemplate.data?.id) {
      requestBody.id = String(cleanedTemplate.data.id);
    }
    
    const response = await apiFetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to save template to API: ${response.status} - ${errorText}`);
      return null;
    }

    const result = await response.json();
    // Handle API response structure - could be various formats Transform back to our format: { id: 12, title: "...", color: "...", data: {...} }
    let savedTemplate = cleanedTemplate; // Start with what we sent (preserves data)
    
    if (result) {
      // Check for nested structure: { success: true, data: { id: 12, data: { title, color, data: {...} } } }
      if (result.data && typeof result.data === 'object' && result.data.id) {
        const apiTemplate = result.data;
        // Check if it has nested data structure (like GET response)
        if (apiTemplate.data && typeof apiTemplate.data === 'object' && 'title' in apiTemplate.data) {
          // Nested structure: { id: 12, data: { title: "...", color: "...", data: {...} } }
          savedTemplate = {
            id: apiTemplate.id, // Use outer numeric id (12, 13, etc.)
            ...apiTemplate.data, // Spread inner data (title, color, data)
          };
        } else {
          // Direct structure: { id: 12, title: "...", color: "...", data: {...} }
          savedTemplate = {
            id: apiTemplate.id,
            title: apiTemplate.title || savedTemplate.title,
            color: apiTemplate.color || savedTemplate.color,
            data: apiTemplate.data || savedTemplate.data || {}, // Preserve data field
          };
        }
      } else if (result.id && result.title) {
        // Response is the template object directly at root level
        savedTemplate = {
          id: result.id,
          title: result.title,
          color: result.color || savedTemplate.color,
          data: result.data || savedTemplate.data || {}, // Preserve data field
        };
      } else if (result.data && typeof result.data === 'object') {
        // Response has data but no id - might be root level structure
        savedTemplate = {
          ...savedTemplate, // Preserve original (especially data field)
          ...result.data, // Override with API response
          data: result.data.data || savedTemplate.data || {}, // Ensure data field is preserved
        };
      }
    }
    
    // Final safety check: ensure data field exists
    if (!savedTemplate.data || typeof savedTemplate.data !== 'object') {
      savedTemplate.data = cleanedTemplate.data || template.data || {};
    }
    
    return savedTemplate;
  } catch (error) {
    console.error('Error saving template to API:', error);
    return null;
  }
};

// Delete template from API
const deleteTemplateFromAPI = async (templateId) => {
  try {
    if (!templateId || (typeof templateId !== 'string' && typeof templateId !== 'number')) {
      console.error('Cannot delete template: invalid templateId');
      return false;
    }
    
    // API requires numeric ID for delete operations - reject string IDs (default templates)
    if (typeof templateId === 'string') {
      const defaultTemplateIds = ['template-1', 'template-2', 'template-3'];
      if (defaultTemplateIds.includes(templateId)) {
        throw new Error('Cannot delete default template. Default templates are read-only.');
      }
      throw new Error('Cannot delete template: API requires numeric ID for deletions.');
    }
    
    const response = await apiFetch(`${TEMPLATES_API_ENDPOINT}/${templateId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to delete template from API: ${response.status} - ${errorText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting template from API:', error);
    return false;
  }
};

// Initialize templates state with defaults (will be loaded from API via action) Start with empty array - templates will be fetched via FETCH_TEMPLATES action
let initialTemplatesState = [];

export const templatesReducer = (state = initialTemplatesState, action) => {
  // Ensure state is always an array
  let currentState;
  try {
    currentState = Array.isArray(state) ? state : [];
  } catch (error) {
    console.error('Error accessing state in templatesReducer:', error);
    currentState = [];
  }
  
  let newState;
  
  try {
    switch (action.type) {
      case 'FETCH_TEMPLATES_SUCCESS':
        // Validate payload is an array
        if (!Array.isArray(action.payload)) {
          console.warn('FETCH_TEMPLATES_SUCCESS: payload is not an array, ignoring');
          return currentState;
        }
        // Validate all templates in the array (accept both numeric and string IDs)
        const validTemplates = action.payload.filter(t => 
          t && 
          typeof t === 'object' && 
          t.id && 
          (typeof t.id === 'string' || typeof t.id === 'number') &&
          t.title &&
          typeof t.title === 'string'
        );
        if (validTemplates.length !== action.payload.length) {
          console.warn('FETCH_TEMPLATES_SUCCESS: Some templates are invalid and will be filtered out');
        }
        return validTemplates.length > 0 ? validTemplates : getDefaultTemplates();
      
      case 'FETCH_TEMPLATES_FAILURE':
        // On failure, return defaults
        console.warn('Failed to fetch templates, using defaults');
        return getDefaultTemplates();
      
      case 'SET_TEMPLATES':
        // Validate payload is an array
        if (!Array.isArray(action.payload)) {
          console.warn('SET_TEMPLATES: payload is not an array, ignoring');
          return currentState;
        }
        // Validate all templates in the array (accept both numeric and string IDs)
        const validTemplatesSet = action.payload.filter(t => 
          t && 
          typeof t === 'object' && 
          t.id && 
          (typeof t.id === 'string' || typeof t.id === 'number') &&
          t.title &&
          typeof t.title === 'string'
        );
        if (validTemplatesSet.length !== action.payload.length) {
          console.warn('SET_TEMPLATES: Some templates are invalid and will be filtered out');
        }
        return validTemplatesSet.length > 0 ? validTemplatesSet : currentState;
      
      case 'ADD_TEMPLATE_SUCCESS':
        // Validate payload exists and has required fields
        if (!action.payload || typeof action.payload !== 'object') {
          console.warn('ADD_TEMPLATE_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        // Accept both numeric (from API) and string (from defaults) IDs
        if (!action.payload.id || (typeof action.payload.id !== 'string' && typeof action.payload.id !== 'number')) {
          console.warn('ADD_TEMPLATE_SUCCESS: template missing valid id, ignoring');
          return currentState;
        }
        // Check if template with same id already exists
        if (currentState.some(t => t && t.id === action.payload.id)) {
          // Update existing instead of adding duplicate
          return currentState.map(template => 
            template && template.id === action.payload.id ? action.payload : template
          );
        }
        return [...currentState, action.payload];
      
      case 'UPDATE_TEMPLATE_SUCCESS':
        // Validate payload exists and has id
        if (!action.payload || typeof action.payload !== 'object') {
          console.warn('UPDATE_TEMPLATE_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        // Accept both numeric (from API) and string (from defaults) IDs
        if (!action.payload.id || (typeof action.payload.id !== 'string' && typeof action.payload.id !== 'number')) {
          console.warn('UPDATE_TEMPLATE_SUCCESS: template missing valid id, ignoring');
          return currentState;
        }
        return currentState.map(template => 
          template && template.id === action.payload.id ? action.payload : template
        ).filter(Boolean); // Remove any null/undefined entries
      
      case 'DELETE_TEMPLATE_SUCCESS':
        // Validate payload is a valid id
        if (!action.payload || (typeof action.payload !== 'string' && typeof action.payload !== 'number')) {
          console.warn('DELETE_TEMPLATE_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        return currentState.filter(template => 
          template && template.id !== action.payload
        );
      
      default:
        return currentState;
    }
  } catch (error) {
    console.error('templatesReducer: Unexpected error:', error);
    // Return current state on any unexpected error
    return currentState;
  }
};

// Action creators with validation
export const setTemplates = (templates) => {
  // Early validation - reducer will also validate
  if (!Array.isArray(templates)) {
    console.warn('setTemplates: templates must be an array');
    return {
      type: 'SET_TEMPLATES',
      payload: [],
    };
  }
  return {
    type: 'SET_TEMPLATES',
    payload: templates,
  };
};

// Thunk to fetch templates from API
export const fetchTemplates = () => async (dispatch) => {
  try {
    const templates = await loadTemplatesFromAPI();
    dispatch({
      type: 'FETCH_TEMPLATES_SUCCESS',
      payload: templates,
    });
  } catch (error) {
    console.error('Error fetching templates:', error);
    dispatch({
      type: 'FETCH_TEMPLATES_FAILURE',
    });
  }
};

// Thunk to add template via API
export const addTemplate = (template) => async (dispatch) => {
  // Early validation
  if (!template || typeof template !== 'object') {
    console.warn('addTemplate: template must be an object');
    return { success: false, error: 'Invalid template data' };
  }
  // Accept both numeric (from API) and string (from defaults) IDs
  if (!template.id || (typeof template.id !== 'string' && typeof template.id !== 'number')) {
    console.warn('addTemplate: template must have a valid id (string or number)');
    return { success: false, error: 'Template must have a valid ID' };
  }
  
  try {
    const savedTemplate = await saveTemplateToAPI(template, 'POST');
    if (savedTemplate) {
      dispatch({
        type: 'ADD_TEMPLATE_SUCCESS',
        payload: savedTemplate,
      });
      return { success: true, template: savedTemplate };
    } else {
      console.error('Failed to add template to API');
      return { success: false, error: 'Failed to add template to API' };
    }
  } catch (error) {
    console.error('Error adding template:', error);
    return { success: false, error: error.message || 'Error adding template' };
  }
};

// Thunk to update template via API
export const updateTemplate = (template) => async (dispatch) => {
  // Early validation
  if (!template || typeof template !== 'object') {
    console.warn('updateTemplate: template must be an object');
    return { success: false, error: 'Invalid template data' };
  }
  // Accept both numeric (from API) and string (from defaults) IDs
  if (!template.id || (typeof template.id !== 'string' && typeof template.id !== 'number')) {
    console.warn('updateTemplate: template must have a valid id (string or number)');
    return { success: false, error: 'Template must have a valid ID' };
  }
  
  try {
    // Check if this is a default template (string ID) before attempting update
    if (typeof template.id === 'string') {
      const defaultTemplateIds = ['template-1', 'template-2', 'template-3'];
      if (defaultTemplateIds.includes(template.id)) {
        return { 
          success: false, 
          error: 'Cannot update default template. Default templates are read-only. Please create a new template or wait for templates to load from the server.' 
        };
      }
    }
    
    const savedTemplate = await saveTemplateToAPI(template, 'PUT');
    if (savedTemplate) {
      dispatch({
        type: 'UPDATE_TEMPLATE_SUCCESS',
        payload: savedTemplate,
      });
      return { success: true, template: savedTemplate };
    } else {
      console.error('Failed to update template in API');
      return { success: false, error: 'Failed to update template in API' };
    }
  } catch (error) {
    console.error('Error updating template:', error);
    return { success: false, error: error.message || 'Error updating template' };
  }
};

// Thunk to delete template via API
export const deleteTemplate = (templateId) => async (dispatch) => {
  // Early validation
  if (!templateId || (typeof templateId !== 'string' && typeof templateId !== 'number')) {
    console.warn('deleteTemplate: templateId must be a string or number');
    return { success: false, error: 'Invalid template ID' };
  }
  
  // Check if this is a default template (string ID) before attempting delete
  if (typeof templateId === 'string') {
    const defaultTemplateIds = ['template-1', 'template-2', 'template-3'];
    if (defaultTemplateIds.includes(templateId)) {
      return { 
        success: false, 
        error: 'Cannot delete default template. Default templates are read-only.' 
      };
    }
  }
  
  try {
    const success = await deleteTemplateFromAPI(templateId);
    if (success) {
      dispatch({
        type: 'DELETE_TEMPLATE_SUCCESS',
        payload: templateId,
      });
      return { success: true };
    } else {
      console.error('Failed to delete template from API');
      return { success: false, error: 'Failed to delete template from API' };
    }
  } catch (error) {
    console.error('Error deleting template:', error);
    return { success: false, error: error.message || 'Error deleting template' };
  }
};

// ==================== Additional Context Presets ====================

// Load additional context presets from API
const loadAdditionalContextPresetsFromAPI = async () => {
  try {
    const response = await apiFetch(ADDITIONAL_CONTEXT_PRESETS_API_ENDPOINT, {
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.warn('Failed to fetch additional context presets from API');
      return [];
    }

    const result = await response.json();
    
    // Handle API response structure: could be { success: true, data: [...] } or direct array
    let rawPresets = [];
    if (result && result.success && Array.isArray(result.data)) {
      rawPresets = result.data;
    } else if (Array.isArray(result)) {
      rawPresets = result;
    } else if (result && Array.isArray(result.data)) {
      rawPresets = result.data;
    }

    // Transform API response: API returns { id: 12, name: "...", content: "..." } or { id: 12, data: { name: "...", content: "..." } }
    const presets = rawPresets
      .filter(p => p && typeof p === 'object' && p.id)
      .map(p => {
        // Handle nested data structure
        if (p.data && typeof p.data === 'object' && ('content' in p.data || 'name' in p.data)) {
          return {
            id: p.id,
            name: p.data.name !== undefined ? (p.data.name || '') : (p.name !== undefined ? (p.name || '') : ''),
            content: p.data.content || p.content || ''
          };
        }
        // Direct structure - preserve null names
        return {
          id: p.id,
          name: p.name !== undefined ? (p.name || '') : '',
          content: p.content || ''
        };
      })
      .filter(p => p.content || p.name); // Only include presets with content or name

    return presets;
  } catch (error) {
    console.error('Error loading additional context presets from API:', error);
    return [];
  }
};

// Save additional context preset to API (for add/update operations)
const saveAdditionalContextPresetToAPI = async (preset, method = 'POST') => {
  try {
    // Validate preset before saving
    if (!preset || typeof preset !== 'object') {
      console.error('Cannot save preset: invalid preset object');
      return null;
    }
    
    // Name is optional - if provided, it must be a non-empty string
    if (preset.name !== null && preset.name !== undefined && (typeof preset.name !== 'string' || !preset.name.trim())) {
      console.error('Cannot save preset: if name is provided, it must be a non-empty string');
      return null;
    }
    
    if (!preset.content || typeof preset.content !== 'string' || !preset.content.trim()) {
      console.error('Cannot save preset: content is required');
      return null;
    }
    
    // For PATCH (update), API requires numeric ID
    if (method === 'PATCH' && typeof preset.id === 'string') {
      const error = 'Cannot update preset with string ID. Please create a new preset or wait for presets to load from the server.';
      console.error(error);
      throw new Error(error);
    }
    
    // For PATCH (update), use the numeric id in the URL
    const url = method === 'PATCH' 
      ? `${ADDITIONAL_CONTEXT_PRESETS_API_ENDPOINT}/${preset.id}`
      : ADDITIONAL_CONTEXT_PRESETS_API_ENDPOINT;
    
    // Prepare request body: API expects { name: "...", content: "..." }
    // Include name only if it's provided and not empty
    const requestBody = {
      content: preset.content.trim()
    };
    
    // Only include name if it's provided and not empty
    if (preset.name && typeof preset.name === 'string' && preset.name.trim()) {
      requestBody.name = preset.name.trim();
    } else {
      requestBody.name = null; // Explicitly set to null if not provided
    }
    
    const response = await apiFetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to save preset to API: ${response.status} - ${errorText}`);
      return null;
    }

    const result = await response.json();
    
    // Handle API response structure
    let savedPreset = preset; // Start with what we sent
    
    if (result) {
      // Check for nested structure: { success: true, data: { id: 12, name: "...", content: "..." } }
      if (result.data && typeof result.data === 'object' && result.data.id) {
        const apiPreset = result.data;
        if (apiPreset.data && typeof apiPreset.data === 'object' && ('content' in apiPreset.data || 'name' in apiPreset.data)) {
          // Nested structure: { id: 12, data: { name: "...", content: "..." } }
          savedPreset = {
            id: apiPreset.id,
            name: apiPreset.data.name || preset.name,
            content: apiPreset.data.content || preset.content
          };
        } else {
          // Direct structure: { id: 12, name: "...", content: "..." }
          savedPreset = {
            id: apiPreset.id,
            name: apiPreset.name || preset.name,
            content: apiPreset.content || preset.content
          };
        }
      } else if (result.id && (result.content || result.name)) {
        // Response is the preset object directly at root level
        savedPreset = {
          id: result.id,
          name: result.name || preset.name,
          content: result.content || preset.content
        };
      } else if (result.id) {
        // Response has id but name/content might be nested
        savedPreset = {
          id: result.id,
          name: result.name || result.data?.name || preset.name,
          content: result.content || result.data?.content || preset.content
        };
      }
    }
    
    return savedPreset;
  } catch (error) {
    console.error('Error saving preset to API:', error);
    return null;
  }
};

// Delete additional context preset from API
const deleteAdditionalContextPresetFromAPI = async (presetId) => {
  try {
    if (!presetId || (typeof presetId !== 'string' && typeof presetId !== 'number')) {
      console.error('Cannot delete preset: invalid presetId');
      return false;
    }
    
    const response = await apiFetch(`${ADDITIONAL_CONTEXT_PRESETS_API_ENDPOINT}/${presetId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to delete preset from API: ${response.status} - ${errorText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting preset from API:', error);
    return false;
  }
};

// Initialize additional context presets state
let initialAdditionalContextPresetsState = [];

export const additionalContextPresetsReducer = (state = initialAdditionalContextPresetsState, action) => {
  let currentState;
  try {
    currentState = Array.isArray(state) ? state : [];
  } catch (error) {
    console.error('Error accessing state in additionalContextPresetsReducer:', error);
    currentState = [];
  }
  
  let newState;
  
  try {
    switch (action.type) {
      case 'FETCH_ADDITIONAL_CONTEXT_PRESETS_SUCCESS':
        if (!Array.isArray(action.payload)) {
          console.warn('FETCH_ADDITIONAL_CONTEXT_PRESETS_SUCCESS: payload is not an array, ignoring');
          return currentState;
        }
        const validPresets = action.payload.filter(p => 
          p && 
          typeof p === 'object' && 
          p.id && 
          (typeof p.id === 'string' || typeof p.id === 'number') &&
          // Allow null or empty name, but if name exists it must be a string
          (p.name === null || p.name === undefined || (typeof p.name === 'string')) &&
          p.content &&
          typeof p.content === 'string'
        );
        // If payload is explicitly an empty array, return empty array (localStorage cleared)
        // Otherwise, if no valid presets found, keep current state (might be a validation issue)
        return action.payload.length === 0 ? [] : (validPresets.length > 0 ? validPresets : currentState);
      
      case 'FETCH_ADDITIONAL_CONTEXT_PRESETS_FAILURE':
        console.warn('Failed to fetch additional context presets');
        return currentState;
      
      case 'ADD_ADDITIONAL_CONTEXT_PRESET_SUCCESS':
        if (!action.payload || typeof action.payload !== 'object') {
          console.warn('ADD_ADDITIONAL_CONTEXT_PRESET_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        if (!action.payload.id || (typeof action.payload.id !== 'string' && typeof action.payload.id !== 'number')) {
          console.warn('ADD_ADDITIONAL_CONTEXT_PRESET_SUCCESS: preset missing valid id, ignoring');
          return currentState;
        }
        // Allow null/undefined name, but if name exists it must be a string
        if (action.payload.name !== null && action.payload.name !== undefined && typeof action.payload.name !== 'string') {
          console.warn('ADD_ADDITIONAL_CONTEXT_PRESET_SUCCESS: preset name must be a string if provided, ignoring');
          return currentState;
        }
        // Check if preset with same id already exists
        if (currentState.some(p => p && p.id === action.payload.id)) {
          // Update existing instead of adding duplicate
          return currentState.map(preset => 
            preset && preset.id === action.payload.id ? action.payload : preset
          );
        }
        return [...currentState, action.payload];
      
      case 'UPDATE_ADDITIONAL_CONTEXT_PRESET_SUCCESS':
        if (!action.payload || typeof action.payload !== 'object') {
          console.warn('UPDATE_ADDITIONAL_CONTEXT_PRESET_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        if (!action.payload.id || (typeof action.payload.id !== 'string' && typeof action.payload.id !== 'number')) {
          console.warn('UPDATE_ADDITIONAL_CONTEXT_PRESET_SUCCESS: preset missing valid id, ignoring');
          return currentState;
        }
        // Allow null/undefined name, but if name exists it must be a string
        if (action.payload.name !== null && action.payload.name !== undefined && typeof action.payload.name !== 'string') {
          console.warn('UPDATE_ADDITIONAL_CONTEXT_PRESET_SUCCESS: preset name must be a string if provided, ignoring');
          return currentState;
        }
        return currentState.map(preset => 
          preset && preset.id === action.payload.id ? action.payload : preset
        );
      
      case 'DELETE_ADDITIONAL_CONTEXT_PRESET_SUCCESS':
        if (!action.payload || (typeof action.payload !== 'string' && typeof action.payload !== 'number')) {
          console.warn('DELETE_ADDITIONAL_CONTEXT_PRESET_SUCCESS: invalid payload, ignoring');
          return currentState;
        }
        return currentState.filter(preset => preset && preset.id !== action.payload);
      
      default:
        return currentState;
    }
  } catch (error) {
    console.error('Error in additionalContextPresetsReducer:', error);
    return currentState;
  }
};

// Thunk to fetch additional context presets from API
export const fetchAdditionalContextPresets = () => async (dispatch) => {
  try {
    const presets = await loadAdditionalContextPresetsFromAPI();
    dispatch({
      type: 'FETCH_ADDITIONAL_CONTEXT_PRESETS_SUCCESS',
      payload: presets,
    });
  } catch (error) {
    console.error('Error fetching additional context presets:', error);
    dispatch({
      type: 'FETCH_ADDITIONAL_CONTEXT_PRESETS_FAILURE',
    });
  }
};

// Thunk to add additional context preset via API
export const addAdditionalContextPreset = (preset) => async (dispatch) => {
  if (!preset || typeof preset !== 'object') {
    console.warn('addAdditionalContextPreset: preset must be an object');
    return { success: false, error: 'Invalid preset data' };
  }
  if (!preset.content || typeof preset.content !== 'string' || !preset.content.trim()) {
    console.warn('addAdditionalContextPreset: preset must have content');
    return { success: false, error: 'Preset content is required' };
  }
  
  try {
    const savedPreset = await saveAdditionalContextPresetToAPI(preset, 'POST');
    if (savedPreset) {
      dispatch({
        type: 'ADD_ADDITIONAL_CONTEXT_PRESET_SUCCESS',
        payload: savedPreset,
      });
      return { success: true, preset: savedPreset };
    } else {
      console.error('Failed to add preset to API');
      return { success: false, error: 'Failed to add preset to API' };
    }
  } catch (error) {
    console.error('Error adding preset:', error);
    return { success: false, error: error.message || 'Error adding preset' };
  }
};

// Thunk to update additional context preset via API
export const updateAdditionalContextPreset = (preset) => async (dispatch) => {
  if (!preset || typeof preset !== 'object') {
    console.warn('updateAdditionalContextPreset: preset must be an object');
    return { success: false, error: 'Invalid preset data' };
  }
  if (!preset.id || (typeof preset.id !== 'string' && typeof preset.id !== 'number')) {
    console.warn('updateAdditionalContextPreset: preset must have a valid id (string or number)');
    return { success: false, error: 'Preset must have a valid ID' };
  }
  // Name is optional - if provided, it must be a non-empty string
  if (preset.name !== null && preset.name !== undefined && (typeof preset.name !== 'string' || !preset.name.trim())) {
    console.warn('updateAdditionalContextPreset: if name is provided, it must be a non-empty string');
    return { success: false, error: 'If name is provided, it must be a non-empty string' };
  }
  if (!preset.content || typeof preset.content !== 'string' || !preset.content.trim()) {
    console.warn('updateAdditionalContextPreset: preset must have content');
    return { success: false, error: 'Preset content is required' };
  }
  
  try {
    const savedPreset = await saveAdditionalContextPresetToAPI(preset, 'PATCH');
    if (savedPreset) {
      dispatch({
        type: 'UPDATE_ADDITIONAL_CONTEXT_PRESET_SUCCESS',
        payload: savedPreset,
      });
      return { success: true, preset: savedPreset };
    } else {
      console.error('Failed to update preset to API');
      return { success: false, error: 'Failed to update preset to API' };
    }
  } catch (error) {
    console.error('Error updating preset:', error);
    return { success: false, error: error.message || 'Error updating preset' };
  }
};

// Thunk to delete additional context preset via API
export const deleteAdditionalContextPreset = (presetId) => async (dispatch) => {
  if (!presetId || (typeof presetId !== 'string' && typeof presetId !== 'number')) {
    console.warn('deleteAdditionalContextPreset: presetId must be a string or number');
    return { success: false, error: 'Invalid preset ID' };
  }
  
  try {
    const success = await deleteAdditionalContextPresetFromAPI(presetId);
    if (success) {
      dispatch({
        type: 'DELETE_ADDITIONAL_CONTEXT_PRESET_SUCCESS',
        payload: presetId,
      });
      return { success: true };
    } else {
      console.error('Failed to delete preset from API');
      return { success: false, error: 'Failed to delete preset from API' };
    }
  } catch (error) {
    console.error('Error deleting preset:', error);
    return { success: false, error: error.message || 'Error deleting preset' };
  }
};

// Export tab actions
export {
  createTab,
  deleteTab,
  switchTab,
  updateTabOrder,
  updateTabEditableData,
  updateTabValidationErrors,
  updateTabTaricErrors,
  updateTabSelectedAgent,
  updateTabSelectedSender,
  updateTabSelectedReceiver,
  updateTabStatus,
  updateTabIsDirty,
  migrateArrayToTabs,
  resetTabs,
};

// Export workflow session actions
export {
  createWorkflowSession,
  deleteWorkflowSession,
  switchWorkflowSession,
  updateWorkflowSessionState,
  updateWorkflowSessionName,
  addToProcessingQueue,
  removeFromProcessingQueue,
  setCurrentlyProcessingSession,
  clearProcessingQueue,
};

// Export UI actions
export {
  setShowBackToDashboardButton,
  setRateLimit,
};

const rootReducer = combineReducers({
  user: userReducer,
  agents: agentsReducer,
  senders: sendersReducer,
  receivers: receiversReducer,
  documents: documentsReducer,
  ui: uiReducer,
  preparedJson: preparedJsonReducer,
  company: companyReducer,
  validationErrors: validationErrorsReducer,
  selectedDeclaration: selectedDeclarationReducer,
  creditStats: creditStatsReducer,
  products: productsReducer,
  groupedTaricJsonOutputs: groupedTaricJsonOutputsReducer,
  suggestions: suggestionsReducer,
  templates: templatesReducer,
  additionalContextPresets: additionalContextPresetsReducer,
  tabs: tabsReducer,
  workflowSessions: workflowSessionsReducer,
});

export default rootReducer;
